/**
 * CUSTOMERS AND SITES (item 2).
 *
 * The register, and behind each row the customer with his sites. A customer is
 * opened, not edited in the list: a change to a name or a GSTIN asks for a
 * reason, because the documents already issued carry the old one and somebody
 * will ask why they differ.
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { api, type Views } from '../api.js';
import { useAsk, useRun, go, routeQuery } from '../hooks.js';
import type { Readiness, ReadinessStep } from '../../../src/views/customers.js';
import { Banner, Button, Card, Count, EmptyState, Kpi, Loading, Money, Pill, Register, Select, Seg, Serial, Text, TextArea, Check, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { Account } from './billing.js';
import { NewCustomer } from './customer-new.js';
import { actionWords, orderStanding, challanStanding } from '../standing.js';
import { count, date, days, money, rupeesToPaise, today, when, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';
import { can, type Session } from '../api.js';

const MESSAGES = screenById('customers')!.messages;

export function Customers({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') {
    // F07 — the guided way in; ?resume=<id> picks up where a person stopped. 11 Oct — the
    // ONE FORM (CTS1's Add New Customer) is the default; the guided way stays at ?guided=1.
    const resume = routeQuery('resume');
    if (resume || routeQuery('guided')) return <Onboard resumeId={resume && /^\d+$/.test(resume) ? Number(resume) : null} />;
    return <NewCustomer />;
  }
  if (id) return <OneCustomer customerId={Number(id)} session={session} />;
  return <CustomerRegister session={session} />;
}

function CustomerRegister({ session }: { session: Session }) {
  const [q, setQ] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [sort, setSort] = useState<'name' | 'owed' | 'held' | 'activity'>('name');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('customers.list', { ...(q.trim() ? { q: q.trim() } : {}), ...(onlyActive ? { active: true } : {}),
                                                         page, pageSize: 50, sort, dir: sort === 'name' ? 'asc' : 'desc' }), [q, onlyActive, sort, page], MESSAGES);
  const money$ = can(session, 'view.account');
  type Row = Views['customers.list']['result']['rows'][number];
  // 11 Oct — CTS1's customer list: the figures the office opens the list FOR, on the row.
  const columns: readonly Column<Row>[] = [
    { key: 'name', head: 'Customer', cell: (r) => <><strong>{r.name}</strong><div className="muted">{r.gstin ?? 'no GSTIN'}{r.state ? ` · ${r.state}` : ''}{r.activeSites > 1 ? ` · ${count(r.activeSites)} sites` : ''}</div></> },
    { key: 'held', head: 'Held', right: true, cell: (r) => <>{r.cylindersOut === 0 ? <span className="muted">—</span> : <strong>{count(r.cylindersOut)}</strong>}{r.ownOnSite > 0 ? <div className="muted" style={{ color: 'var(--primary, #001078)' }}>+{count(r.ownOnSite)} own</div> : null}</> },
    { key: 'overdue', head: 'Overdue', right: true, cell: (r) => r.overdueOut > 0 ? <Pill tone="danger">{count(r.overdueOut)}</Pill> : <span className="muted">—</span> },
    ...(money$ ? [{ key: 'owed', head: 'Owed', right: true, cell: (r: Row) => r.payablePaise > 0 ? <strong><Money paise={r.payablePaise} /></strong> : <span className="muted">—</span> } as Column<Row>] : []),
    { key: 'trips', head: 'Trips', right: true, cell: (r) => r.trips > 0 ? count(r.trips) : <span className="muted">—</span> },
    { key: 'last', head: 'Last activity', cell: (r) => r.lastActivityOn ? date(r.lastActivityOn) : <span className="muted">No activity</span> },
    { key: 'status', head: '', cell: (r) => r.active ? null : <Pill tone="muted">closed</Pill> },
  ];
  const totals = asked.data ? asked.data.rows.reduce((a, r) => ({ held: a.held + r.cylindersOut, owed: a.owed + r.payablePaise }), { held: 0, owed: 0 }) : null;

  return (
    <Page title="Customers" sub="Who we sell to — what they hold, what they owe, and when we last dealt with them."
          actions={can(session, 'customer.manage') ? <Button kind="primary" onClick={() => go('#/customers/new')}>New customer</Button> : null}>
      <div className="toolbar">
        <div className="field grow">
          <label htmlFor="q">Search</label>
          <input id="q" type="text" value={q} placeholder="Name or GSTIN" onChange={(e: { target: { value: string } }) => { setQ(e.target.value); setPage(1); }} />
        </div>
        <Select name="sort" label="Order" value={sort} onChange={(v) => { setSort(v as typeof sort); setPage(1); }}
                options={[{ value: 'name', label: 'By name' }, { value: 'held', label: 'Most cylinders held' }, ...(money$ ? [{ value: 'owed', label: 'Most owed' }] : []), { value: 'activity', label: 'Most recent' }]} />
        <Check name="active" checked={onlyActive} onChange={setOnlyActive} label="Only customers still on the books" />
      </div>
      <Card>
        {asked.loading && !asked.data ? <Loading what="customers" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : <><Register columns={columns} rows={asked.data!.rows} empty="No customer matches that."
                      onOpen={(r) => go(`#/customers/${r.customerId}`)}
                      totals={<span>Showing <b>{count(asked.data!.rows.length)}</b> of <b>{count(asked.data!.page.total)}</b>{totals ? <> · on this page <b>{count(totals.held)}</b> cylinders held{money$ ? <> · <b><Money paise={totals.owed} /></b> owed</> : null}</> : null}</span>} />
              <Pager page={asked.data!.page} onPage={setPage} /></>}
      </Card>
    </Page>
  );
}

export const TABS = [
  { value: 'overview', label: 'Overview', needs: 'view.customers' },
  // 10 Oct — the hub: the customer's money lives HERE (the Money screen is the company-wide view)
  { value: 'money', label: 'Money', needs: 'view.account' },
  { value: 'holdings', label: 'Holdings', needs: 'view.account' },
  // 10 Oct — the owner's question: how many times has this customer's own cylinder been filled
  { value: 'cylinders', label: 'Their cylinders', needs: 'view.jobs' },
  { value: 'challans', label: 'Challans', needs: 'view.dispatch' },
  { value: 'bills', label: 'Bills', needs: 'view.account' },
  { value: 'payments', label: 'Payments', needs: 'view.account' },
  { value: 'orders', label: 'Orders', needs: 'view.orders' },
  { value: 'quotations', label: 'Quotations', needs: 'view.quotes' },
  { value: 'jobs', label: 'Customer Jobs', needs: 'view.jobs' },
  { value: 'record', label: 'Record', needs: 'view.customers' },
] as const;
export function tabsFor(session: Session) { return TABS.filter((t) => can(session, t.needs)).map(({ value, label }) => ({ value, label })); }
/** The tab a URL's `?tab=` asks for, when it is one this session may read; `overview` otherwise. */
export function chosenTab(wanted: string | null, allowed: readonly { value: string }[]): string {
  return wanted !== null && allowed.some((t) => t.value === wanted) ? wanted : 'overview';
}

/* ================================================================== 11 Oct 2026: CTS1's customer page, on CTS2's tabs
 *
 * The owner: "fix customer page completely". CTS1's AccountPage: a header with the facts and
 * the actions (+ Payment, + Quotation, More), a KPI strip, and two-level tabs — Overview ·
 * Sales · Cylinders · Billing · Ledger. CTS2 had the same material as twelve flat tabs; it is
 * now grouped the same way, the leaf tab still named in the URL so every link keeps working.
 */
const GROUPS: { id: string; label: string; leaves: string[] }[] = [
  { id: 'overview', label: 'Overview', leaves: ['overview'] },
  { id: 'sales', label: 'Sales', leaves: ['quotations', 'orders'] },
  { id: 'cylinders', label: 'Cylinders', leaves: ['holdings', 'challans', 'cylinders', 'jobs'] },
  { id: 'billing', label: 'Billing', leaves: ['bills'] },
  { id: 'ledger', label: 'Ledger', leaves: ['money', 'payments'] },
  { id: 'details', label: 'Details', leaves: ['record'] },
];
const LEAF_LABEL: Record<string, string> = { quotations: 'Quotations', orders: 'Orders', holdings: 'Held now', challans: 'DC · RC', cylinders: 'Their cylinders', jobs: 'Filling jobs', bills: 'Bills & statement', money: 'Account', payments: 'Payments', record: 'Record' };

function OneCustomer({ customerId, session }: { customerId: number; session: Session }) {
  const asked = useAsk(() => api.view('customers.get', { customerId }), [customerId], MESSAGES);
  const readiness = useAsk(() => api.view('customers.readiness', { customerId }), [customerId], MESSAGES);
  const money$ = can(session, 'view.account');
  const bal = useAsk(() => (money$ ? api.view('account.balance', { customerId }) : Promise.resolve(null)), [customerId, money$], MESSAGES);
  const sum = useAsk(() => api.view('customers.summary', { customerId }), [customerId], MESSAGES);
  const notReady = !!readiness.data && !readiness.data.ready;
  const allowed = tabsFor(session);
  const tab = chosenTab(routeQuery('tab'), allowed);
  const [, force] = useState(0);
  useEffect(() => { const on = () => force((n) => n + 1); globalThis.addEventListener?.('hashchange', on); return () => globalThis.removeEventListener?.('hashchange', on); }, []);
  const setTab = (v: string) => go(`#/customers/${customerId}?tab=${v}`);

  if (asked.loading) return <Page title="Customer"><Loading what="the customer" /></Page>;
  if (asked.error) return <Page title="Customer"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const primary = d.sites.find((x) => x.isPrimary) ?? d.sites[0];
  const allowedIds = new Set<string>(allowed.map((t) => t.value));
  const groups = GROUPS.map((g) => ({ ...g, leaves: g.leaves.filter((l) => allowedIds.has(l)) })).filter((g) => g.leaves.length > 0);
  const group = groups.find((g) => g.leaves.includes(tab)) ?? groups[0]!;
  const b = bal.data; const sm = sum.data ?? null;

  return (
    <Page title={d.customer.name}
          sub={[primary?.address, d.customer.gstin ? `GST ${d.customer.gstin}` : 'no GSTIN on file', d.customer.state].filter(Boolean).join(' · ')}
          actions={<>
            {notReady && can(session, 'customer.manage')
              ? <Button kind="primary" onClick={() => go(`#/customers/new?resume=${customerId}`)}>Continue onboarding</Button> : null}
            {money$ ? <Button kind={notReady ? 'ghost' : 'primary'} onClick={() => setTab('money')}>+ Payment</Button> : null}
            {can(session, 'quote.draft') ? <Button onClick={() => go(`#/quotes?new=1&customer=${customerId}`)}>+ Quotation</Button> : null}
            {can(session, 'order.create') ? <Button onClick={() => go(`#/orders?new=1&customer=${customerId}`)}>+ Order</Button> : null}
            {can(session, 'challan.create') ? <Button onClick={() => go(`#/move?dir=out&customer=${customerId}`)}>Dispatch</Button> : null}
            {can(session, 'customer.manage') ? <Button onClick={() => setTab('record')}>Edit</Button> : null}
          </>}>
      {/* 12 Oct — A FIGURE THAT COULD NOT BE READ IS NOT ZERO. customers.summary
          failed for weeks with a type error and this page showed "Holding billed
          ₹0.00" as though it had asked and been told nothing was owed. */}
      {sum.error ? <Banner kind="error" title="The trading figures could not be read" detail={`${sum.error.title}${sum.error.detail ? ` — ${sum.error.detail}` : ''} The outstanding, paid and deposit figures below are still this customer's own.`} /> : null}
      {/* THE STRIP (CTS1): outstanding · held now · billed · paid · not yet billed · SD */}
      {money$ && b ? (
        <div className="kpis" data-customer-kpis>
          <Kpi label="Outstanding" value={money(b.payablePaise)} tone={b.payablePaise > 0 ? 'money' : 'empty'} note="billed, less paid and credited" href={`#/customers/${customerId}?tab=bills`} />
          <Kpi label="Held now" value={count(b.parts.cylindersOut)} tone="primary" note={sm ? `${count(sm.openTrips)} open · ${count(sm.overdueOut)} overdue` : (b.oldestOpenDays === null ? 'none out' : `oldest out ${days(b.oldestOpenDays)}`)} href={`#/customers/${customerId}?tab=holdings`} />
          <Kpi label="Holding billed" value={sm ? money(sm.holdingBilledPaise) : '—'} note={sm ? `${money(sm.holdingThisMonthPaise)} this month · rent+GST` : 'could not be read'} href={`#/customers/${customerId}?tab=bills`} />
          <Kpi label="Gas billed" value={sm ? money(sm.gasBilledPaise) : '—'} note={sm ? (sm.byGas.map((g) => g.gasService).join(', ') || 'no gas billed') : 'could not be read'} href={`#/customers/${customerId}?tab=bills`} />
          <Kpi label="Paid" value={money(b.parts.paidPaise)} tone="good" note={b.recentPayments[0] ? `last ${date(b.recentPayments[0].paidOn)}` : 'nothing yet'} href={`#/customers/${customerId}?tab=payments`} />
          {b.parts.unbilledPaise > 0 ? <Kpi label="Not yet billed" value={money(b.parts.unbilledPaise)} tone="empty" note="earned, not on a bill yet" /> : null}
          <Kpi label="SD held" value={money(b.depositHeldPaise)} note="returnable" href={`#/customers/${customerId}?tab=money`} />
        </div>
      ) : null}
      {/* THE BILLING BAR (CTS1): the terms a dispatch is priced from, in one line */}
      <TermsBar d={d} sm={sm} />
      {/* 0174 — CTS1's pricing banner: which quote governs the rates, and until when */}
      <GoverningBanner d={d} customerId={customerId} session={session} />

      <div className="seg" role="tablist" aria-label="Customer" style={{ marginBottom: 6 }}>
        {groups.map((g) => (
          <button key={g.id} type="button" className={`seg-btn${group.id === g.id ? ' on' : ''}`} onClick={() => setTab(g.leaves[0]!)}>{g.label}</button>
        ))}
      </div>
      {group.leaves.length > 1 ? (
        <div className="seg" role="tablist" aria-label={group.label} style={{ marginBottom: 12 }}>
          {group.leaves.map((l) => (
            <button key={l} type="button" className={`seg-btn small${tab === l ? ' on' : ''}`} onClick={() => setTab(l)}>{LEAF_LABEL[l] ?? l}{sm && l === 'holdings' ? ` (${count(sm.heldNow)})` : sm && l === 'challans' ? ` (${count(sm.dc)} · ${count(sm.rc)})` : sm && l === 'cylinders' ? ` (${count(sm.ownOnSite)})` : ''}</button>
          ))}
        </div>
      ) : null}
      {/* the old flat Seg is kept for the walk and the palette: hidden, same values */}
      <div style={{ display: 'none' }}><Seg name="customer-tab" value={tab} onChange={setTab} options={allowed} /></div>

      {tab === 'overview' ? <OverviewTab customerId={customerId} session={session} readiness={readiness.data ?? null} onTab={setTab} bal={b ?? null} balLoading={bal.loading} sm={sm} /> : null}
      {tab === 'money' ? <Account customerId={customerId} session={session} customerName={d.customer.name} /> : null}
      {tab === 'holdings' ? <HoldingsTab customerId={customerId} /> : null}
      {tab === 'cylinders' ? <TheirCylindersTab customerId={customerId} /> : null}
      {tab === 'challans' ? <ChallansTab customerId={customerId} /> : null}
      {tab === 'bills' ? <BillsTab customerId={customerId} session={session} /> : null}
      {tab === 'payments' ? <PaymentsTab customerId={customerId} /> : null}
      {tab === 'orders' ? <OrdersTab customerId={customerId} /> : null}
      {tab === 'quotations' ? <QuotationsTab customerId={customerId} /> : null}
      {tab === 'jobs' ? <JobsTab customerId={customerId} session={session} /> : null}
      {tab === 'record' ? <RecordTab customerId={customerId} session={session} d={d} reload={() => { asked.reload(); readiness.reload(); }} onReadiness={readiness.reload} readiness={readiness.data ?? null} /> : null}
    </Page>
  );
}

function GoverningBanner({ d, customerId, session }: { d: Views['customers.get']['result']; customerId: number; session: Session }) {
  const g = d.governing;
  const newQuote = can(session, 'quote.draft') ? <Button kind="primary" onClick={() => go(`#/quotes/new?customer=${customerId}`)}>+ New quote</Button> : null;
  const open = g.quotationId ? <a href={`#/quotes/${g.quotationId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/quotes/${g.quotationId}`); }}>{g.number ?? `quotation #${g.quotationId}`}</a> : null;
  const line = (
    g.state === 'none' ? <><strong>⚠ No governing quote.</strong> {d.terms.missing ? 'No rates are agreed at all — ' : 'The rates on the card were entered by hand — '}issue and approve a quote to lock this customer’s gas and holding rates.</>
    : g.state === 'valid' ? <><strong>✓ Pricing valid till {date(g.goodUntil)}</strong> · {open}{g.effectiveFrom ? `, effective ${date(g.effectiveFrom)}` : ''}</>
    : g.state === 'renewing' ? <><strong>↻ Pricing renewing monthly · good to {date(g.goodUntil)}</strong> · {open} agreed until {date(g.validUntil)}; the rates roll on until a new quote is approved</>
    : <><strong>⚠ Pricing ended {date(g.validUntil)}</strong> · {open}; renewal is off, so gas billing waits for a new approved quote</>
  );
  return (
    <div className={`banner ${g.state === 'valid' ? 'good' : g.state === 'renewing' ? 'info' : 'warn'}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }} data-governing={g.state}>
      <div>{line}</div>
      {g.state === 'valid' ? null : newQuote}
    </div>
  );
}

function TermsBar({ d, sm }: { d: Views['customers.get']['result']; sm: Views['customers.summary']['result'] | null }) {
  const rates = (d.terms.gasRates ?? {}) as Record<string, number>;
  const card = (d.terms.holdingCard ?? null) as { freeDays?: number; slabs?: { fromDay: number; toDay: number | null; ratePaise: number }[] } | null;
  const rateWords = Object.entries(rates).map(([g, p]) => `${g} ${money(p)}/cyl`).join(' · ');
  return (
    <div className="muted" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', margin: '2px 0 12px', fontSize: 13 }}>
      <span><b>Rates:</b> {d.terms.missing ? <span style={{ color: 'var(--danger, #b91c1c)' }}>none agreed</span> : d.terms.lapsed ? <span style={{ color: 'var(--danger, #b91c1c)' }}>lapsed {date(d.terms.lapsedOn)}</span> : rateWords || '—'}</span>
      <span><b>Holding:</b> {card ? `${card.freeDays ?? 0} free days${card.slabs?.length ? `, ${card.slabs.length} tier${card.slabs.length === 1 ? '' : 's'}` : ''}` : '—'}</span>
      <span><b>Freight:</b> {d.terms.freightPaise ? money(d.terms.freightPaise) : '—'}</span>
      <span><b>Sites:</b> {count(d.sites.filter((x) => x.active).length)}</span>
      <span><b>Since:</b> {date(d.customer.createdAt)}</span>
      {d.customer.creditLimitPaise ? <span><b>Credit limit:</b> {money(d.customer.creditLimitPaise)}</span> : null}
      {sm ? <><span><b>First transaction:</b> {sm.firstOn ? date(sm.firstOn) : 'none'}</span><span><b>Last:</b> {sm.lastOn ? date(sm.lastOn) : 'none'}</span><span><b>Movements:</b> {count(sm.movements)}</span><span><b>Challans:</b> {count(sm.dc)} DC · {count(sm.rc)} RC</span></> : null}
    </div>
  );
}

/** OVERVIEW (CTS1): money & credit, cylinders & deposit, and the last few challans. */
function OverviewTab({ customerId, session, readiness, onTab, bal, balLoading, sm }: { customerId: number; session: Session; readiness: Readiness | null; onTab: (t: string) => void; bal: Views['account.balance']['result'] | null; balLoading: boolean; sm: Views['customers.summary']['result'] | null }) {
  const money$ = can(session, 'view.account');
  const recent = useAsk(() => (can(session, 'view.dispatch') ? api.view('challans.list', { customerId, page: 1, pageSize: 8, sort: 'date', dir: 'desc' }) : Promise.resolve(null)), [customerId], MESSAGES);
  return (<>
    <ReadinessCard readiness={readiness} customerId={customerId} />
    <div className="grid two">
      {money$ ? (balLoading && !bal ? <Loading what="the account" /> : bal ? (
        <Card title="Money & credit" sub="every part, so a figure can be argued with" actions={<Button small onClick={() => onTab('money')}>Open the ledger</Button>}>
          <div className="detail-grid">
            <div><div className="k">Outstanding</div><div className="v"><strong><Money paise={bal.payablePaise} /></strong></div></div>
            <div><div className="k">Billed</div><div className="v"><Money paise={bal.parts.billedPaise} /></div></div>
            <div><div className="k">Paid</div><div className="v"><Money paise={bal.parts.paidPaise} /></div></div>
            <div><div className="k">Credits</div><div className="v"><Money paise={bal.parts.creditsPaise} /></div></div>
            <div><div className="k">Corrections</div><div className="v"><Money paise={bal.parts.correctionsPaise} /></div></div>
            <div><div className="k">Not yet billed</div><div className="v"><Money paise={bal.parts.unbilledPaise} /></div></div>
          </div>
          {!bal.agrees ? <Banner kind="warn" title="The recomputed balance and the figure on file disagree." detail={`On file ${money(bal.onFile?.payablePaise ?? null)} · recomputed ${money(bal.payablePaise)}. Tell a manager.`} /> : null}
        </Card>
      ) : null) : <Card><EmptyState title="What this customer owes is on the Billing side." detail="Ask a manager for the accounts function to see it here." /></Card>}
      {bal ? (
        <Card title="Cylinders & deposit" actions={<Button small onClick={() => onTab('holdings')}>Held now</Button>}>
          <div className="detail-grid">
            <div><div className="k">Held now</div><div className="v"><strong>{count(bal.parts.cylindersOut)}</strong></div></div>
            <div><div className="k">Overdue</div><div className="v" style={{ color: (sm?.overdueOut ?? 0) > 0 ? 'var(--danger, #b91c1c)' : undefined }}>{count(sm?.overdueOut ?? 0)}{bal.oldestOpenDays !== null ? <span className="muted"> · oldest {days(bal.oldestOpenDays)}</span> : null}</div></div>
            <div><div className="k">Their cylinders with us</div><div className="v">{count(sm?.ownOnSite ?? 0)}</div></div>
            <div><div className="k">Cylinders out, at replacement</div><div className="v"><Money paise={bal.parts.cylindersHeldPaise} /></div></div>
            <div><div className="k">SD held</div><div className="v"><Money paise={bal.depositHeldPaise} /></div></div>
            <div><div className="k">Deposit covers</div><div className="v">{bal.parts.cylindersHeldPaise > 0 ? `${Math.min(999, Math.round(100 * bal.depositHeldPaise / bal.parts.cylindersHeldPaise))}%` : '—'}</div></div>
            <div><div className="k">Owed plus cylinders</div><div className="v"><Money paise={bal.exposurePaise} /></div></div>
          </div>
        </Card>
      ) : null}
    </div>
    {sm && sm.byGas.length > 0 ? (
      <Card title="Transactions by gas type" sub="held now, trips ever, and what was sold">
        <Register rows={sm.byGas} empty="" columns={[
          { key: 'g', head: 'Gas', cell: (g: { gasService: string }) => <strong>{words(g.gasService)}</strong> },
          { key: 'h', head: 'Held now', right: true, cell: (g: { heldNow: number }) => count(g.heldNow) },
          { key: 't', head: 'Trips', right: true, cell: (g: { trips: number }) => count(g.trips) },
          { key: 's', head: '₹ sold (incl GST)', right: true, cell: (g: { soldPaise: number }) => <Money paise={g.soldPaise} /> },
        ]} />
      </Card>
    ) : null}
    {recent.data ? (
      <Card title="Recent activity" sub="the last challans, out and in" actions={<Button small onClick={() => onTab('challans')}>All DC · RC</Button>}>
        {recent.data.rows.length === 0 ? <p className="muted">No challan yet.</p> : recent.data.rows.map((r: { challanId: number; type: string; number: string | null; eventDate: string; lineCount: number; status: string }) => (
          <a className="row-link" key={r.challanId} href={`#/challans/${r.challanId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/challans/${r.challanId}`); }}>
            <span><strong>{r.type === 'return' ? 'Return' : 'Dispatch'} {r.number ?? ''}</strong><span>{date(r.eventDate)} · {count(r.lineCount)} cyl · {words(r.status)}</span></span>
            <span className="chev" aria-hidden="true">&rsaquo;</span>
          </a>
        ))}
      </Card>
    ) : null}
  </>);
}

/** WHAT THEY ARE HOLDING NOW: the cylinders and whether rent is running. */
function HoldingsTab({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const h = useAsk(() => api.view('account.holdings', { customerId, page, pageSize: 50 }), [customerId, page], MESSAGES);
  if (h.loading && !h.data) return <Card><Loading what="holdings" /></Card>;
  if (h.error) return <Card><Banner kind="error" title={h.error.title} /></Card>;
  const d = h.data!;
  return (
    <Card title="What they are holding" sub={`${count(d.summary.cylindersOut)} cylinders · ${money(d.summary.replacementValuePaise)} to replace — rent runs after the card's free days; the exact accrual is on the Bills tab`}>
      <Register columns={[
          { key: 'cyl', head: 'Cylinder', cell: (r) => <Serial>{r.internalId}</Serial> },
          { key: 'gas', head: 'Gas', cell: (r) => words(r.gasService) },
          { key: 'out', head: 'Out since', cell: (r) => <>{date(r.outDate)}{r.outChallan ? <div className="muted">{r.outChallan}</div> : null}</> },
          { key: 'days', head: 'Days out', right: true, cell: (r) => count(r.daysOut) },
          { key: 'site', head: 'Site', cell: (r) => r.siteName ?? '—' },
          { key: 'billed', head: 'Billed to', cell: (r) => date(r.billedUpTo) },
        ] as readonly Column<(typeof d.rows)[number]>[]}
        rows={d.rows} empty="This customer is holding nothing." onOpen={(r) => go(`#/cylinders/${r.cylinderId}`)}
        totals={<span>By gas <b>{Object.entries(d.summary.byGas).map(([g, n]) => `${words(g)} ${count(n)}`).join(' · ') || '—'}</b></span>} />
      <Pager page={d.page} onPage={setPage} />
    </Card>
  );
}

/** EVERY DELIVERY AND RETURN — newest first, the last three months by default. */
/** 10 Oct — THE FILL LEDGER: the customer's own cylinders, each with how many times it was filled, when last, how much, and the last batch and check. */
function TheirCylindersTab({ customerId }: { customerId: number }) {
  const asked = useAsk(() => api.view('customers.cylinderFills', { customerId }), [customerId], MESSAGES);
  if (asked.loading && !asked.data) return <Card><Loading what="their cylinders" /></Card>;
  if (asked.error) return <Card><Banner kind="error" title={asked.error.title} /></Card>;
  const d = asked.data!;
  const qtyText = (q: Record<string, number>) => [q['m3'] ? `${q['m3']} Nm³` : '', q['kg'] ? `${q['kg']} kg` : ''].filter(Boolean).join(' + ') || '—';
  return (
    <Card title="Their own cylinders — the fill ledger"
          sub={`${count(d.totals.cylinders)} cylinder${d.totals.cylinders === 1 ? '' : 's'} · ${count(d.totals.fills)} fill${d.totals.fills === 1 ? '' : 's'} · ${qtyText(d.totals.qtyByUnit)} put in`}>
      <Register
        columns={[
          { key: 'cyl', head: 'Cylinder', cell: (r: { internalId: string; serialNo: string }) => <><Serial>{r.internalId}</Serial><div className="muted">{r.serialNo}</div></> },
          { key: 'gas', head: 'Gas', cell: (r: { gasService: string }) => words(r.gasService) },
          { key: 'where', head: 'Where it is', cell: (r: { state: string | null }) => <Pill>{words(r.state ?? 'unknown')}</Pill> },
          { key: 'times', head: 'Times filled', right: true, cell: (r: { timesFilled: number }) => <strong>{count(r.timesFilled)}</strong> },
          { key: 'last', head: 'Last filled', cell: (r: { lastFilledOn: string | null }) => r.lastFilledOn ? date(r.lastFilledOn) : <span className="muted">never</span> },
          { key: 'qty', head: 'Gas put in', right: true, cell: (r: { qtyByUnit: Record<string, number> }) => qtyText(r.qtyByUnit) },
          { key: 'batch', head: 'Last batch · check', cell: (r: { lastBatch: string | null; lastQc: string | null }) => r.lastBatch ? <>{r.lastBatch}{r.lastQc ? <> · <Pill tone={r.lastQc === 'passed' ? 'good' : 'danger'}>{r.lastQc}</Pill></> : null}</> : <span className="muted">—</span> },
        ]}
        rows={d.rows} onOpen={(r) => go(`#/cylinders/${r.cylinderId}`)}
        empty="This customer has no cylinders of their own on record. A customer's cylinder is registered as theirs when it first comes in on a job." />
    </Card>
  );
}

function ChallansTab({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const [from] = useState(() => addDaysIso(today(), -90));
  const [to] = useState(() => today());
  const c = useAsk(() => api.view('challans.list', { customerId, from, to, page, pageSize: 50 }), [customerId, from, to, page], MESSAGES);
  return (
    <Card title="Challans" sub="every delivery and return for this customer, newest first"
          actions={<a className="btn ghost small" href={api.challanRegisterUrl(customerId, { from, to })} target="_blank" rel="noreferrer">Print the register</a>}>
      {c.loading && !c.data ? <Loading what="challans" /> : c.error ? <Banner kind="error" title={c.error.title} /> : (<>
        <Register columns={[
            { key: 'number', head: 'Number', cell: (r) => r.number ?? '—' },
            { key: 'type', head: 'Type', cell: (r) => words(r.type) },
            { key: 'date', head: 'Date', cell: (r) => date(r.eventDate) },
            { key: 'site', head: 'Site', cell: (r) => r.siteName },
            { key: 'standing', head: 'Standing', cell: (r) => { const st = challanStanding(r.type, r.status, r.journey); return <Pill tone={st.tone}>{st.word}</Pill>; } },
          ] as readonly Column<(typeof c.data extends null ? never : NonNullable<typeof c.data>['rows'][number])>[]}
          rows={c.data?.rows ?? []} empty="No challan for this customer in this window."
          onOpen={(r) => go(`#/challans/${r.challanId}`)} />
        <Pager page={c.data!.page} onPage={setPage} />
      </>)}
    </Card>
  );
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthWord = (m: string) => `${MONTH_NAMES[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const KIND_WORDS: Record<string, string> = { delivery_memo: 'Delivery memo', monthly: 'Monthly holding', event: 'Bill' };

/** 'YYYY-MM' plus k months, k of either sign — the browser's own copy of the arithmetic (the service owns the definition). */
export function addMonthsUi(m: string, k: number): string {
  const y = Number(m.slice(0, 4)); const mo = Number(m.slice(5, 7)) - 1 + k;
  return `${y + Math.floor(mo / 12)}-${String(((mo % 12) + 12) % 12 + 1).padStart(2, '0')}`;
}
function addDaysIso(iso: string, k: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + k);
  return d.toISOString().slice(0, 10);
}

type StatementLine = Views['account.statement']['result']['months'][number]['lines'][number];

function lineWhat(l: StatementLine): string {
  if (l.kind === 'bill') return KIND_WORDS[l.bill?.billKind ?? ''] ?? 'Bill';
  if (l.kind === 'payment') return l.payment?.reversed ? 'Payment reversed' : 'Payment';
  if (l.kind === 'credit_note') return 'Credit note';
  return 'Correction';
}

function lineFor(l: StatementLine): React.ReactNode {
  if (l.bill) {
    const what = l.bill.challanNumber ?? (l.bill.periodStart ? `${date(l.bill.periodStart)} to ${date(l.bill.periodEnd)}` : l.bill.eventDescription ?? '—');
    const parts = ([['gas', l.bill.parts.gasPaise], ['holding', l.bill.parts.holdingPaise], ['damage', l.bill.parts.damagePaise],
                     ['freight', l.bill.parts.freightPaise], ['other', l.bill.parts.otherPaise]] as [string, number][])
      .filter(([, v]) => v !== 0).map(([k, v]) => `${k} ${money(v)}`).join(' · ');
    return <>{what}{parts ? <div className="muted">{parts}{l.bill.parts.gstPaise ? ` · GST ${money(l.bill.parts.gstPaise)}` : ''}</div> : null}
             {l.bill.residuePaise > 0 ? <div className="muted">{money(l.bill.residuePaise)} still unpaid</div> : <div className="muted">paid in full</div>}</>;
  }
  if (l.payment) return <>{words(l.payment.method)}{l.payment.reference ? ` · ${l.payment.reference}` : ''}{l.payment.reverseReason ? <div className="muted">{l.payment.reverseReason}</div> : null}</>;
  return l.number ?? '—';
}

/** THE STATEMENT, MONTH BY MONTH — pure, so it is testable without a session or a network. */
export function StatementTable({ s, onOpenBill }: { s: Views['account.statement']['result']; onOpenBill?: (billId: number) => void }) {
  return (<>
    <div className="kpis">
      <Kpi label={`Owed on 1 ${monthWord(s.from)}`} value={money(s.openingPaise)} tone="muted" />
      <Kpi label="Billed in the period" value={money(s.months.reduce((a, m) => a + m.billedPaise, 0))} tone="money" />
      <Kpi label="Paid in the period" value={money(s.months.reduce((a, m) => a + m.paidPaise, 0))} tone="good" />
      <Kpi label={`Owed at the end of ${monthWord(s.to)}`} value={money(s.closingPaise)} tone="danger" />
    </div>
    {s.months.map((m) => (
      <Card key={m.month} title={monthWord(m.month)}
            sub={`opened at ${money(m.openingPaise)} · billed ${money(m.billedPaise)} · paid ${money(m.paidPaise)}${m.creditedPaise ? ` · credited ${money(m.creditedPaise)}` : ''}${m.correctedPaise ? ` · corrected ${money(m.correctedPaise)}` : ''} · closed at ${money(m.closingPaise)}`}>
        <Register columns={[
            { key: 'on', head: 'Date', cell: (l: StatementLine) => date(l.on) },
            { key: 'what', head: 'What', cell: (l: StatementLine) => lineWhat(l) },
            { key: 'number', head: 'Number', cell: (l: StatementLine) => l.number ?? '—' },
            { key: 'for', head: 'For', cell: (l: StatementLine) => lineFor(l) },
            { key: 'amount', head: 'Amount', right: true, cell: (l: StatementLine) => <Money paise={l.amountPaise} plain /> },
            { key: 'running', head: 'Balance', right: true, cell: (l: StatementLine) => <Money paise={l.runningPaise} plain /> },
          ] as readonly Column<StatementLine>[]}
          rows={m.lines} empty="Nothing happened this month."
          {...(onOpenBill ? { onOpen: (l: StatementLine) => { if (l.bill) onOpenBill(l.bill.billId); } } : {})} />
      </Card>
    ))}
    {s.accruing.trips.length > 0 ? (
      <Card title="Holding charges running, not yet billed" sub={`${money(s.accruing.totalPaise)} so far · ${s.accruing.note}`}>
        <Register columns={[
            { key: 'cyl', head: 'Cylinder', cell: (t) => <Serial>{t.internalId}</Serial> },
            { key: 'gas', head: 'Gas', cell: (t) => words(t.gasService) },
            { key: 'out', head: 'Out since', cell: (t) => date(t.outDate) },
            { key: 'days', head: 'Days', right: true, cell: (t) => count(t.days) },
            { key: 'rate', head: 'Rate now / day', right: true, cell: (t) => <Money paise={t.ratePaiseNow} plain /> },
            { key: 'acc', head: 'Accrued', right: true, cell: (t) => <Money paise={t.accruedPaise} plain /> },
          ] as readonly Column<(typeof s.accruing.trips)[number]>[]} rows={s.accruing.trips} empty="" />
      </Card>) : null}
    {s.unbilled.length > 0 ? (
      <Card title="Charges not yet on a bill" sub="these go on the next bill">
        <Register columns={[
            { key: 'what', head: 'What', cell: (u) => words(u.chargeType) },
            { key: 'desc', head: 'Description', cell: (u) => u.description },
            { key: 'ch', head: 'Challan', cell: (u) => u.challanNumber ?? '—' },
            { key: 'amt', head: 'Amount', right: true, cell: (u) => <Money paise={u.amountPaise} plain /> },
          ] as readonly Column<(typeof s.unbilled)[number]>[]} rows={s.unbilled} empty="" />
      </Card>) : null}
    {s.tdsNote ? <Banner kind="info" title="Some payments are a little short of their bills." detail={s.tdsNote} /> : null}
    <p className="muted">Bills are proformas and delivery memos, not tax invoices. As of {date(s.asOf)}.</p>
  </>);
}

/** THE BILLS TAB — the statement for a chosen span, and the A4 sheet. */
const BILL_KIND_OPTIONS = [{ value: '', label: 'Every kind' }, { value: 'delivery_memo', label: 'Delivery memo' }, { value: 'monthly', label: 'Monthly holding' }, { value: 'event', label: 'Bill' }];
const BILL_STATUS_OPTIONS = [{ value: '', label: 'Every status' }, { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }];

export function BillsTab({ customerId, session }: { customerId: number; session: Session }) {
  const thisMonth = today().slice(0, 7);
  const [from, setFrom] = useState(addMonthsUi(thisMonth, -5));
  const [to, setTo] = useState(thisMonth);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [openBillId, setOpenBillId] = useState<number | null>(null);
  const mayOpenBill = can(session, 'view.bills');
  const s = useAsk(() => api.view('account.statement', { customerId, from, to }), [customerId, from, to], MESSAGES);
  const list = useAsk(() => api.view('bills.list', {
    customerId, from, to, page, pageSize: 20,
    ...(kind ? { kind: kind as 'delivery_memo' | 'monthly' | 'event' } : {}), ...(status ? { status: status as 'draft' | 'sent' } : {}),
  }), [customerId, from, to, kind, status, page], MESSAGES);

  return (<>
    {mayOpenBill ? (<>
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        <Text name="from" label="From month" type="month" value={from} onChange={setFrom} />
        <Text name="to" label="To month" type="month" value={to} onChange={setTo} />
        <Select name="bill-kind" label="Kind" value={kind} onChange={(v) => { setKind(v); setPage(1); }} options={BILL_KIND_OPTIONS} />
        <Select name="bill-status" label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={BILL_STATUS_OPTIONS} />
        <a className="btn ghost small" href={api.statementUrl(customerId, { from, to })} target="_blank" rel="noreferrer">Download A4</a>
      </div>

      <Card title="Every bill in this window" sub="filter by kind or status; open one to see its lines">
        {list.loading && !list.data ? <Loading what="bills" /> : list.error ? <Banner kind="error" title={list.error.title} /> : (<>
          <Register columns={[
              { key: 'number', head: 'Number', cell: (r) => r.number ?? '—' },
              { key: 'kind', head: 'Kind', cell: (r) => KIND_WORDS[r.kind] ?? words(r.kind) },
              { key: 'for', head: 'For', cell: (r) => r.challanNumber ?? (r.periodStart ? `${date(r.periodStart)} to ${date(r.periodEnd)}` : r.eventDescription ?? '—') },
              { key: 'sent', head: 'Sent', cell: (r) => date(r.sentOn) },
              { key: 'total', head: 'Total', right: true, cell: (r) => <Money paise={r.totalPaise} plain /> },
              { key: 'residue', head: 'Still unpaid', right: true, cell: (r) => <Money paise={r.residuePaise} plain /> },
              { key: 'status', head: 'Status', cell: (r) => <Pill tone={r.status === 'sent' ? 'good' : 'muted'}>{words(r.status)}</Pill> },
              // DEF-151 — the paper itself. A bill the office cannot hand over
              // is a bill the customer cannot pay against; bills sent before
              // 0085 have no document and show a dash rather than a dead link.
              { key: 'paper', head: 'Paper', cell: (r) => r.documentId === null
                  ? <span className="muted">—</span>
                  : <a className="btn ghost small" href={api.documentUrl(r.documentId)} target="_blank" rel="noreferrer"
                       onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}>Download</a> },
            ] as readonly Column<(typeof list.data extends null ? never : NonNullable<typeof list.data>['rows'][number])>[]}
            rows={list.data?.rows ?? []} empty="No bill matches this window and filter."
            onOpen={(r: { billId: number }) => setOpenBillId(r.billId)} />
          <Pager page={list.data!.page} onPage={setPage} />
        </>)}
      </Card>

      {openBillId !== null ? <BillLinesCard billId={openBillId} onClose={() => setOpenBillId(null)} /> : null}
    </>) : null}

    {s.loading && !s.data ? <Card><Loading what="the statement" /></Card>
      : s.error ? <Card><Banner kind="error" title={s.error.title} {...(s.error.detail ? { detail: s.error.detail } : {})} /></Card>
      : <StatementTable s={s.data!} {...(mayOpenBill ? { onOpenBill: setOpenBillId } : {})} />}
  </>);
}

/** WHAT ONE BILL WAS FOR, LINE BY LINE — the drill-in from either the filter strip or the statement, drawn only when this session may read a bill's lines. */
function BillLinesCard({ billId, onClose }: { billId: number; onClose: () => void }) {
  const b = useAsk(() => api.view('bills.get', { billId }), [billId], MESSAGES);
  return (
    <Card title={b.data ? `What ${b.data.bill.number ?? 'this bill'} was for` : 'What this bill was for'}
          actions={<Button small onClick={onClose}>Close</Button>}>
      {b.loading ? <Loading what="the bill" /> : b.error ? <Banner kind="error" title={b.error.title} /> : (
        <Register columns={[
            { key: 'desc', head: 'Description', cell: (l) => l.description },
            { key: 'for', head: 'For', cell: (l) => l.sourceRef ?? words(l.sourceKind) },
            { key: 'amount', head: 'Amount', right: true, cell: (l) => <Money paise={l.amountPaise} plain /> },
          ] as readonly Column<(typeof b.data extends null ? never : NonNullable<typeof b.data>['lines'][number])>[]}
          rows={(b.data?.lines ?? []).filter((l) => !l.voided)} empty="No line on this bill." />
      )}
    </Card>
  );
}

/** EVERY PAYMENT — take a payment and reverse one stay on Billing. */
function PaymentsTab({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const p = useAsk(() => api.view('payments.list', { customerId, page, pageSize: 50 }), [customerId, page], MESSAGES);
  return (
    <Card title="Payments" sub="take a payment and reverse one are on the Billing screen"
          actions={<Button small onClick={() => go(`#/billing/${customerId}`)}>Open Billing</Button>}>
      {p.loading && !p.data ? <Loading what="payments" /> : p.error ? <Banner kind="error" title={p.error.title} /> : (<>
        <Register columns={[
            { key: 'paid', head: 'Paid on', cell: (r) => date(r.paidOn) },
            { key: 'receipt', head: 'Receipt', cell: (r) => r.receiptNumber ?? '—' },
            { key: 'method', head: 'How', cell: (r) => <>{(r.instrument ?? r.method).toUpperCase()}{r.instrumentNo ? <div className="muted">{r.instrumentNo}</div> : null}{r.reference ? <div className="muted">{r.reference}</div> : null}</> },
            { key: 'purpose', head: 'Towards', cell: (r) => r.purpose === 'security_deposit' ? <Pill tone="money">SD held</Pill> : r.purpose === 'advance' ? <Pill>advance</Pill> : 'bills' },
            { key: 'amount', head: 'Amount', right: true, cell: (r) => <Money paise={r.amountPaise} plain /> },
            { key: 'status', head: 'Status', cell: (r) => r.reversed ? <Pill tone="danger">reversed</Pill> : <Pill tone="good">received</Pill> },
          ] as readonly Column<(typeof p.data extends null ? never : NonNullable<typeof p.data>['rows'][number])>[]}
          rows={p.data?.rows ?? []} empty="No payment recorded for this customer." />
        <Pager page={p.data!.page} onPage={setPage} />
      </>)}
    </Card>
  );
}

/** THIS CUSTOMER'S ORDERS, one row each. */
function OrdersTab({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const o = useAsk(() => api.view('orders.list', { customerId, current: true, page, pageSize: 50 }), [customerId, page], MESSAGES);
  return (
    <Card title="Orders">
      {o.loading && !o.data ? <Loading what="orders" /> : o.error ? <Banner kind="error" title={o.error.title} /> : (<>
        <Register columns={[
            { key: 'order', head: 'Order', cell: (r) => `#${r.orderId}` },
            { key: 'requested', head: 'Requested', cell: (r) => date(r.requestedDate) },
            { key: 'site', head: 'Site', cell: (r) => r.siteName },
            { key: 'standing', head: 'Standing', cell: (r) => { const st = orderStanding(r.status, r.fulfilment); return <Pill tone={st.tone}>{st.word}</Pill>; } },
          ] as readonly Column<(typeof o.data extends null ? never : NonNullable<typeof o.data>['rows'][number])>[]}
          rows={o.data?.rows ?? []} empty="No order for this customer." onOpen={(r) => go(`#/orders/${r.orderId}`)} />
        <Pager page={o.data!.page} onPage={setPage} />
      </>)}
    </Card>
  );
}

/** EVERY OFFER MADE TO THIS CUSTOMER, and what it became. */
function QuotationsTab({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const q = useAsk(() => api.view('quotes.list', { customerId, page, pageSize: 50 }), [customerId, page], MESSAGES);
  return (
    <Card title="Quotations">
      {q.loading && !q.data ? <Loading what="quotations" /> : q.error ? <Banner kind="error" title={q.error.title} /> : (<>
        <Register columns={[
            { key: 'number', head: 'Number', cell: (r) => r.number ?? '—' },
            { key: 'revision', head: 'Revision', cell: (r) => count(r.revisionNo) },
            { key: 'issued', head: 'Issued', cell: (r) => date(r.issuedOn) },
            { key: 'valid', head: 'Valid until', cell: (r) => date(r.validUntil) },
            { key: 'status', head: 'Status', cell: (r) => <Pill tone={r.standsOn ? 'good' : 'muted'}>{words(r.status)}</Pill> },
            { key: 'order', head: 'Became order', cell: (r) => r.orderId ? `#${r.orderId}` : '—' },
          ] as readonly Column<(typeof q.data extends null ? never : NonNullable<typeof q.data>['rows'][number])>[]}
          rows={q.data?.rows ?? []} empty="No quotation for this customer." onOpen={(r) => go(`#/quotes/${r.quotationId}`)} />
        <Pager page={q.data!.page} onPage={setPage} />
      </>)}
    </Card>
  );
}

/**
 * CUSTOMER JOBS (0075) — where each of this customer's cylinders in for
 * work stands. `received` and `inspected` only: filling, QC and the
 * Customer Fill Dispatch are later slices and this tab does not offer them.
 */
type PendingWorkRow = { serialNo: string; gasService: string; workKinds: string[] };
const WORK_KINDS = ['fill', 'test', 'repair', 'inspect', 'valve_replacement'] as const;

function JobsTab({ customerId, session }: { customerId: number; session: Session }) {
  const [page, setPage] = useState(1);
  const jobs = useAsk(() => api.view('customerJobs.list', { customerId, page, pageSize: 20 }), [customerId, page], MESSAGES);
  const [creating, setCreating] = useState(false);
  const [openJobId, setOpenJobId] = useState<number | null>(null);
  const receiveRunner = useRun(MESSAGES);
  const [receiptNumber, setReceiptNumber] = useState<string | null>(null);

  async function receive(jobId: number) {
    setReceiptNumber(null);
    const ok = await receiveRunner.run(async (retryKey) => {
      const r = await api.command('job.receive', { jobId }, retryKey ? { idempotencyKey: retryKey } : {});
      const v = r.value as { receiptNumber?: string };
      if (v.receiptNumber) setReceiptNumber(v.receiptNumber);
      return r;
    });
    if (ok) jobs.reload();
  }

  return (
    <Card title="Customer Jobs" sub="cylinders received for work, and what has been found on inspection so far"
          actions={can(session, 'job.create') ? <Button small onClick={() => setCreating((v) => !v)}>{creating ? 'Cancel' : 'Log cylinders received'}</Button> : null}>
      {creating ? <NewJobForm customerId={customerId} onDone={() => { setCreating(false); jobs.reload(); }} /> : null}
      {receiveRunner.error ? <Banner kind="error" title={receiveRunner.error.title} {...(receiveRunner.error.detail ? { detail: receiveRunner.error.detail } : {})} /> : null}
      {receiptNumber ? <Banner kind="good" title={`Receipt ${receiptNumber} issued.`} detail="The inward receipt was numbered the moment the cylinders were received." /> : null}
      {jobs.loading && !jobs.data ? <Loading what="jobs" /> : jobs.error ? <Banner kind="error" title={jobs.error.title} /> : (<>
        <Register columns={[
            { key: 'received', head: 'Received', cell: (r) => date(r.receivedDate) },
            { key: 'reference', head: 'Customer reference', cell: (r) => r.customerReference ?? '—' },
            { key: 'cylinders', head: 'Cylinders', right: true, cell: (r) => count(r.cylinderCount) },
            { key: 'status', head: 'Status', cell: (r) => <Pill tone={r.status === 'received' ? 'good' : r.status === 'cancelled' ? 'muted' : 'testing'}>{words(r.status)}</Pill> },
          ] as readonly Column<(typeof jobs.data extends null ? never : NonNullable<typeof jobs.data>['rows'][number])>[]}
          rows={jobs.data?.rows ?? []} empty="No cylinder has been logged in for this customer."
          onOpen={(r) => setOpenJobId(openJobId === r.jobId ? null : r.jobId)} />
        <Pager page={jobs.data!.page} onPage={setPage} />
      </>)}
      {openJobId !== null ? (
        <JobCylindersPanel jobId={openJobId} session={session}
          onReceive={() => { void receive(openJobId); }} receiving={receiveRunner.busy}
          onChanged={() => jobs.reload()}
          job={jobs.data?.rows.find((r) => r.jobId === openJobId) ?? null} />
      ) : null}
    </Card>
  );
}

function NewJobForm({ customerId, onDone }: { customerId: number; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [customerReference, setCustomerReference] = useState('');
  const [receivedDate, setReceivedDate] = useState(today());
  const [rows, setRows] = useState<PendingWorkRow[]>([{ serialNo: '', gasService: '', workKinds: ['inspect'] }]);

  function toggleWork(i: number, kind: string) {
    setRows((rs) => rs.map((r, idx) => idx !== i
      ? r : { ...r, workKinds: r.workKinds.includes(kind) ? r.workKinds.filter((k) => k !== kind) : [...r.workKinds, kind] }));
  }
  function setField(i: number, field: 'serialNo' | 'gasService', v: string) {
    setRows((rs) => rs.map((r, idx) => idx !== i ? r : { ...r, [field]: v }));
  }

  async function submit() {
    const cylinders = rows.filter((r) => r.serialNo.trim()).map((r) => ({
      serialNo: r.serialNo.trim(), gasService: r.gasService.trim(), workKinds: r.workKinds as ('fill' | 'test' | 'repair' | 'inspect' | 'valve_replacement')[],
    }));
    const ok = await runner.run(() => api.command('job.create', {
      customerId, receivedDate, cylinders, ...(customerReference.trim() ? { customerReference: customerReference.trim() } : {}),
    }));
    if (ok) onDone();
  }

  const valid = rows.some((r) => r.serialNo.trim() && r.gasService.trim() && r.workKinds.length > 0);
  return (
    <Card title="A draft — nothing moves until it is received">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Text name="job-reference" label="Customer's own reference" value={customerReference} onChange={setCustomerReference} />
        <Text name="job-received" label="Received on" type="date" value={receivedDate} onChange={setReceivedDate} />
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          <Text name={`job-serial-${i}`} label="Serial number" value={r.serialNo} onChange={(v) => setField(i, 'serialNo', v)} />
          <Text name={`job-gas-${i}`} label="Gas" value={r.gasService} onChange={(v) => setField(i, 'gasService', v)} />
          <div className="field">
            <label>Requested work</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {WORK_KINDS.map((k) => (
                <Check key={k} name={`job-work-${i}-${k}`} checked={r.workKinds.includes(k)} onChange={() => toggleWork(i, k)} label={words(k)} />
              ))}
            </div>
          </div>
        </div>
      ))}
      <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
        <Button small onClick={() => setRows((rs) => [...rs, { serialNo: '', gasService: '', workKinds: ['inspect'] }])}>Add another cylinder</Button>
        <Button small kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !valid}>Save the draft</Button>
      </div>
    </Card>
  );
}

/** A job's cylinders, and — for a received job — the inspection outcome. */
function JobCylindersPanel({ jobId, job, session, onReceive, receiving, onChanged }: {
  jobId: number; job: { status: string } | null; session: Session; onReceive: () => void; receiving: boolean;
  /** the job's own status changes as the work finishes, so the list above reloads too */
  onChanged?: () => void;
}) {
  // job?.status is in the dependency list too: receiving happens on THIS
  // job's row (the jobId does not change) and the custody/contents facts it
  // wrote only exist once the status flips to 'received' — without this the
  // panel kept showing the pre-receiving cylinder facts until some other
  // prop forced a remount.
  const cyls = useAsk(() => api.view('customerJobs.cylinders', { jobId }), [jobId, job?.status], MESSAGES);
  if (cyls.loading && !cyls.data) return <Card><Loading what="the job's cylinders" /></Card>;
  if (cyls.error) return <Card><Banner kind="error" title={cyls.error.title} /></Card>;
  const rows = cyls.data ?? [];
  return (
    <Card title={`Job #${jobId}`}
          actions={job?.status === 'draft' && can(session, 'job.receive')
            ? <Button small kind="primary" onClick={onReceive} disabled={receiving}>Receive — issue the inward receipt</Button> : null}>
      <Register columns={[
          { key: 'serial', head: 'Serial', cell: (r) => r.serialNo ?? <span className="muted">pending registration</span> },
          { key: 'work', head: 'Requested work', cell: (r) => r.requestedWork.map(words).join(', ') || '—' },
          { key: 'custody', head: 'Custody', cell: (r) => r.custodianKind
              ? <span>{r.custodianKind === 'company' ? 'ours' : words(r.custodianKind)}, {words(r.physicalPosition)}</span>
              : <span className="muted">not received yet</span> },
          { key: 'contents', head: 'Contents', cell: (r) => r.contents ? <Pill>{words(r.contents)}</Pill> : <span className="muted">—</span> },
          { key: 'outcome', head: 'Inspection', cell: (r) => r.inspectionOutcome
              ? <Pill tone={r.inspectionOutcome === 'released' ? 'good' : r.inspectionOutcome === 'quarantined' ? 'danger' : 'testing'}>{words(r.inspectionOutcome)}</Pill>
              : <span className="muted">not received yet</span> },
          // 0081 — the work itself, in one column: what went in, whether it
          // passed, and whether it has gone home
          { key: 'work', head: 'Work done', cell: (r) => r.returnedOn
              ? <><Pill tone="good">gone home</Pill><div className="muted">{r.outwardNumber} · {date(r.returnedOn)}</div></>
              : r.qcOutcome
                ? <><Pill tone={r.qcOutcome === 'passed' ? 'good' : 'danger'}>{r.qcOutcome === 'passed' ? 'checked and passed' : 'failed its check'}</Pill>
                    {r.fillGasService ? <div className="muted">{r.fillQuantity} {r.fillQuantityUnit} {words(r.fillGasService)}</div> : null}</>
                : r.filledAt
                  ? <><Pill tone="testing">filled, waiting on its check</Pill>
                      <div className="muted">{r.fillQuantity} {r.fillQuantityUnit} {words(r.fillGasService ?? '')}</div></>
                  : <span className="muted">—</span> },
          { key: 'charged', head: 'Charged', cell: (r) => r.billed ? <Pill tone="good">yes</Pill> : <span className="muted">not yet</span> },
        ] as readonly Column<(typeof rows)[number]>[]}
        rows={rows} empty="No cylinder on this job." />
      {job?.status === 'draft'
        ? rows.filter((r) => r.cylinderId === null && r.serialNo !== null).map((r) => (
            <IdentifyRow key={r.jobCylinderId} jobId={jobId} serialNo={r.serialNo!} rows={rows} session={session} onDone={cyls.reload} />
          ))
        : null}
      {job?.status === 'received'
        ? rows.filter((r) => r.contestedIntakeId !== null && r.contestedIntakeStatus === 'open').map((r) => (
            <ContestedIntakeRow key={`ci-${r.jobCylinderId}`} row={r} session={session} onDone={cyls.reload} />
          ))
        : null}
      {job?.status === 'received'
        ? rows.filter((r) => r.contestedIntakeId !== null && r.contestedIntakeStatus !== 'open').map((r) => (
            <SettledIntakeHistoryRow key={`cih-${r.jobCylinderId}`} row={r} session={session} onDone={cyls.reload} />
          ))
        : null}
      {job?.status === 'received'
        ? rows.filter((r) => r.ownershipDisputed).map((r) => (
            <ResolveDisputeRow key={`d-${r.jobCylinderId}`} jobId={jobId} row={r} session={session} onDone={cyls.reload} />
          ))
        : null}
      {job?.status === 'received'
        ? rows.filter((r) => r.cylinderId !== null && !r.ownershipDisputed && (r.inspectionOutcome === null || r.inspectionOutcome === 'awaiting_inspection')).map((r) => (
            <InspectRow key={r.jobCylinderId} jobId={jobId} cylinderId={r.cylinderId!} onDone={cyls.reload} />
          ))
        : null}
      {/* 0081 — the next step on each cylinder, and only the next one */}
      {/* 0082 — ...AND A SECOND ATTEMPT, once the drum has been made fit for one. `filledAt`
          alone meant a cylinder that failed its check and was then purged could never be
          offered the refill the command has allowed since 0082: the screen showed the
          preparation row for ever and the fill row never again. */}
      {job?.status === 'received'
        ? rows.filter((r) => r.cylinderId !== null && !r.ownershipDisputed && r.inspectionOutcome === 'released'
                             && (r.filledAt === null || r.readyToRefill) && r.returnedOn === null).map((r) => (
            <FillRow key={`f-${r.jobCylinderId}`} jobId={jobId} cylinderId={r.cylinderId!} session={session} onDone={cyls.reload} />
          ))
        : null}
      {job?.status === 'received'
        ? rows.filter((r) => r.cylinderId !== null && r.filledAt !== null && r.qcOutcome === null).map((r) => (
            <QcRow key={`q-${r.jobCylinderId}`} jobId={jobId} cylinderId={r.cylinderId!} session={session} onDone={cyls.reload} />
          ))
        : null}
      {/* 0082 — a cylinder that failed its check is made fit to fill again */}
      {job?.status === 'received'
        ? rows.filter((r) => r.cylinderId !== null && r.qcOutcome === 'failed' && r.returnedOn === null
                             && !r.readyToRefill).map((r) => (
            <PrepareRow key={`p-${r.jobCylinderId}`} jobId={jobId} cylinderId={r.cylinderId!} session={session} onDone={cyls.reload} />
          ))
        : null}
      {job && (job.status === 'received' || job.status === 'returned')
        ? <FinishJobRow jobId={jobId} job={job} rows={rows} session={session} onDone={() => { cyls.reload(); onChanged?.(); }} />
        : null}
    </Card>
  );
}

/** What went into the cylinder: the gas, how much, at what pressure, out of which batch. */
function FillRow({ jobId, cylinderId, session, onDone }: { jobId: number; cylinderId: number; session: Session; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [gas, setGas] = useState('oxygen');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('m3');
  const [pressure, setPressure] = useState('');
  const [batch, setBatch] = useState('');
  const [batchSource, setBatchSource] = useState('');
  if (!can(session, 'job.fill')) return null;

  async function record() {
    const ok = await runner.run((retryKey) => api.command('job.fill', {
      jobId, cylinderId, gasService: gas.trim(), quantity: Number(quantity), quantityUnit: unit as 'kg' | 'm3',
      ...(pressure.trim() ? { pressureBar: Number(pressure) } : {}),
      // 0082 — a batch is a row, not a label. The source is needed only the
      // first time a batch is used; the door says so if it is missing.
      batch: { reference: batch.trim(), ...(batchSource.trim() ? { sourceRef: batchSource.trim() } : {}) },
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setQuantity(''); setPressure(''); setBatchSource(''); onDone(); }
  }
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <span>Fill cylinder #{cylinderId}</span>
      <Text name={`fill-gas-${cylinderId}`} label="Gas" value={gas} onChange={setGas} />
      <Text name={`fill-qty-${cylinderId}`} label="Quantity" value={quantity} onChange={setQuantity} />
      <Select name={`fill-unit-${cylinderId}`} label="Measured in" value={unit} onChange={setUnit}
              options={[{ value: 'm3', label: 'cubic metres' }, { value: 'kg', label: 'kilograms' }]} />
      <Text name={`fill-pressure-${cylinderId}`} label="Pressure (bar)" value={pressure} onChange={setPressure} />
      <Text name={`fill-batch-${cylinderId}`} label="Batch" value={batch} onChange={setBatch}
            help="the same reference is the same batch" />
      <Text name={`fill-batch-source-${cylinderId}`} label="Where the gas came from" value={batchSource} onChange={setBatchSource}
            help="only needed the first time this batch is used" />
      <Button small kind="primary" onClick={() => { void record(); }} disabled={runner.busy || !quantity.trim() || !gas.trim() || !batch.trim()}>
        {runner.busy ? 'Recording…' : 'Record the fill'}
      </Button>
    </div>
  );
}

/** The quality check: purity and pressure against what was asked for, passed or failed in writing. */
function QcRow({ jobId, cylinderId, session, onDone }: { jobId: number; cylinderId: number; session: Session; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [purity, setPurity] = useState('');
  const [pressure, setPressure] = useState('');
  const [reason, setReason] = useState('');
  if (!can(session, 'job.qc')) return null;

  async function record(outcome: 'passed' | 'failed') {
    const ok = await runner.run((retryKey) => api.command('job.qc', {
      jobId, cylinderId, outcome,
      ...(purity.trim() ? { purityPct: Number(purity) } : {}),
      ...(pressure.trim() ? { pressureBar: Number(pressure) } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setPurity(''); setPressure(''); setReason(''); onDone(); }
  }
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <span>Check cylinder #{cylinderId}</span>
      <Text name={`qc-purity-${cylinderId}`} label="Purity (%)" value={purity} onChange={setPurity} />
      <Text name={`qc-pressure-${cylinderId}`} label="Pressure (bar)" value={pressure} onChange={setPressure} />
      <Text name={`qc-reason-${cylinderId}`} label="What was wrong (if it failed)" value={reason} onChange={setReason} />
      <Button small kind="primary" onClick={() => { void record('passed'); }} disabled={runner.busy}>It passed</Button>
      <Button small kind="danger" onClick={() => { void record('failed'); }} disabled={runner.busy || !reason.trim()}>It failed</Button>
    </div>
  );
}

/**
 * MAKING A REJECTED CYLINDER FIT TO FILL AGAIN (0082).
 *
 * The gas that failed its check is still in the vessel, so a refill is refused
 * until somebody has purged, evacuated or vented it and said so. This is that
 * record: what was done, why, and — taken from the register rather than from
 * memory — what the cylinder held when the work started. A purge or an
 * evacuation is the one kind of evidence D265 accepts for "verified empty".
 */
function PrepareRow({ jobId, cylinderId, session, onDone }: { jobId: number; cylinderId: number; session: Session; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [method, setMethod] = useState('purged');
  const [reason, setReason] = useState('');
  if (!can(session, 'job.prepare')) return null;

  async function record() {
    const ok = await runner.run((retryKey) => api.command('job.prepare', {
      jobId, cylinderId, method: method as 'purged' | 'evacuated' | 'vented' | 'valve_replaced' | 'other', reason: reason.trim(),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setReason(''); onDone(); }
  }
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <span>Cylinder #{cylinderId} failed its check</span>
      <Select name={`prepare-method-${cylinderId}`} label="What was done to it" value={method} onChange={setMethod}
              options={[{ value: 'purged', label: 'Purged' }, { value: 'evacuated', label: 'Evacuated' },
                        { value: 'vented', label: 'Vented' }, { value: 'valve_replaced', label: 'Valve replaced' },
                        { value: 'other', label: 'Something else' }]} />
      <Text name={`prepare-reason-${cylinderId}`} label="Why, and how" value={reason} onChange={setReason} />
      <Button small kind="primary" onClick={() => { void record(); }} disabled={runner.busy || !reason.trim()}>
        {runner.busy ? 'Recording…' : 'Record the preparation'}
      </Button>
    </div>
  );
}

/**
 * THE TWO THINGS LEFT: send them home, and charge for the work.
 *
 * Both are per-job rather than per-cylinder, because that is how the yard does
 * them: one note for the load going back, one charge run for the work done.
 */
function FinishJobRow({ jobId, job, rows, session, onDone }: {
  jobId: number; job: { status: string }; rows: Views['customerJobs.cylinders']['result']; session: Session; onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [vehicle, setVehicle] = useState('');
  const [receivedByName, setReceivedByName] = useState('');
  const [outwardNumber, setOutwardNumber] = useState<string | null>(null);
  const [charged, setCharged] = useState<string | null>(null);

  const stillHere = rows.filter((r) => r.cylinderId !== null && r.returnedOn === null);
  /**
   * WHAT CAN ACTUALLY GO HOME TODAY.
   *
   * A job often holds one cylinder that is finished and another that is
   * quarantined, in dispute, or filled and still waiting on its check. Asking
   * the door to send the whole job would be refused because of the one that
   * cannot travel, and the finished cylinders would sit in the plant for a
   * reason that is nothing to do with them. So the note carries the ones that
   * are ready, by name, and the screen says how many are held back and why —
   * the door still refuses anything that should not travel, and this only
   * stops the yard being told "no" for the wrong cylinder.
   */
  const ready = stillHere.filter((r) => !r.ownershipDisputed
    && r.inspectionOutcome === 'released'
    && r.qcOutcome !== 'failed'
    && (r.filledAt === null || r.qcOutcome === 'passed'));
  const heldBack = stillHere.filter((r) => !ready.includes(r));
  const unbilledFilled = rows.filter((r) => r.filledAt !== null && !r.billed);

  async function sendHome() {
    const ok = await runner.run(async (retryKey) => {
      const r = await api.command('job.return', {
        jobId,
        cylinderIds: ready.map((x) => x.cylinderId!),
        ...(vehicle.trim() ? { vehicle: vehicle.trim() } : {}),
        ...(receivedByName.trim() ? { receivedByName: receivedByName.trim() } : {}),
      }, retryKey ? { idempotencyKey: retryKey } : {});
      const v = r.value as { outwardNumber?: string };
      if (v.outwardNumber) setOutwardNumber(v.outwardNumber);
      return r;
    });
    if (ok) onDone();
  }
  async function charge() {
    const ok = await runner.run(async (retryKey) => {
      const r = await api.command('job.bill', { jobId }, retryKey ? { idempotencyKey: retryKey } : {});
      const v = r.value as { totalPaise?: number };
      if (typeof v.totalPaise === 'number') setCharged(String(v.totalPaise));
      return r;
    });
    if (ok) onDone();
  }

  if (stillHere.length === 0 && unbilledFilled.length === 0 && !outwardNumber && !charged) return null;
  const whyHeld = (r: Views['customerJobs.cylinders']['result'][number]) => r.ownershipDisputed ? 'ownership in dispute'
    : r.qcOutcome === 'failed' ? 'failed its check'
    : r.inspectionOutcome === 'quarantined' ? 'quarantined'
    : r.inspectionOutcome !== 'released' ? 'waiting on its inspection'
    : 'filled and waiting on its check';
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {outwardNumber ? <Banner kind="good" title={`Customer Fill Dispatch ${outwardNumber} issued.`} detail="The cylinders on it are back in their owner's hands, and the paper says who took them." /> : null}
      {charged ? <Banner kind="good" title="The work on this job has been charged." detail="Priced from this customer's own agreed rates — it appears on his account and on his next bill." /> : null}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {ready.length > 0 && can(session, 'job.return') ? (<>
          <Text name="outward-vehicle" label="Vehicle" value={vehicle} onChange={setVehicle} />
          <Text name="outward-receiver" label="Who took them" value={receivedByName} onChange={setReceivedByName} />
          <Button small kind="primary" onClick={() => { void sendHome(); }} disabled={runner.busy}>
            {runner.busy ? 'Working…' : `Send ${count(ready.length)} home — issue the Customer Fill Dispatch`}
          </Button>
        </>) : null}
        {unbilledFilled.length > 0 && can(session, 'job.bill') ? (
          <Button small onClick={() => { void charge(); }} disabled={runner.busy}>Charge for the work</Button>
        ) : null}
      </div>
      {heldBack.length > 0 ? (
        <p className="muted" style={{ marginBottom: 0 }}>
          {count(heldBack.length)} staying here for now: {heldBack.map((r) => `${r.serialNo ?? `cylinder ${r.cylinderId}`} (${whyHeld(r)})`).join(', ')}.
        </p>
      ) : null}
    </div>
  );
}

/**
 * WHICH PHYSICAL CYLINDER IS THIS? (the owner's ruling, 26 Sep.)
 *
 * A serial is an attribute, never an identity (P2): two owners can hold
 * different vessels stamped alike, and so can one owner. When the serial on a
 * draft line names more than one cylinder — or names one that is out of service
 * — receiving refuses and asks the yard to say which vessel is standing in
 * front of it. Until this existed the only way to answer was the command door,
 * which is no answer at all for the person actually holding the cylinder.
 *
 * Choosing a candidate re-sends the whole draft through `job.edit`, because a
 * draft's lines are replaced as a set; every other line is sent back exactly as
 * it stands, so identifying one cylinder changes nothing about the others.
 */
function IdentifyRow({ jobId, serialNo, rows, session, onDone }: {
  jobId: number; serialNo: string; rows: Views['customerJobs.cylinders']['result']; session: Session; onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [open, setOpen] = useState(false);
  const found = useAsk(() => open ? api.view('cylinders.search', { q: serialNo, pageSize: 20 }) : Promise.resolve(null), [open, serialNo], MESSAGES);
  if (!can(session, 'job.edit')) return null;

  async function identify(cylinderId: number) {
    const ok = await runner.run(() => api.command('job.edit', {
      jobId,
      cylinders: rows.map((r) => r.serialNo === serialNo && r.cylinderId === null
        ? { cylinderId, workKinds: r.requestedWork as ('fill' | 'test' | 'repair' | 'inspect' | 'valve_replacement')[] }
        : r.cylinderId !== null
          ? { cylinderId: r.cylinderId, workKinds: r.requestedWork as ('fill' | 'test' | 'repair' | 'inspect' | 'valve_replacement')[] }
          : { serialNo: r.serialNo!, workKinds: r.requestedWork as ('fill' | 'test' | 'repair' | 'inspect' | 'valve_replacement')[] }),
    }));
    if (ok) { setOpen(false); onDone(); }
  }

  const candidates = (found.data?.rows ?? []).filter((c) => c.serialNo.toUpperCase() === serialNo.toUpperCase());
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>Serial {serialNo}</span>
        <Button small onClick={() => setOpen((v) => !v)}>{open ? 'Close' : 'Which cylinder is this?'}</Button>
      </div>
      {open ? (
        <div style={{ marginTop: 8 }}>
          {found.loading ? <Loading what="cylinders with that serial" /> : candidates.length === 0
            ? <p className="muted">No cylinder on the register carries that serial, so receiving will register it as a new one.</p>
            : (<>
                <Banner kind="info" title="More than one vessel can carry one serial."
                        detail="Say which one is standing in the yard. Choosing another owner's cylinder records the arrival and holds it as disputed — it never moves the ownership." />
                <Register columns={[
                    { key: 'id', head: 'Cylinder', cell: (c: { internalId: string }) => <Serial>{c.internalId}</Serial> },
                    { key: 'owner', head: 'Owner', cell: (c: { ownerKind: string; ownerCustomerId: number | null }) =>
                        c.ownerKind === 'company' ? 'ours' : `customer ${c.ownerCustomerId ?? '—'}` },
                    { key: 'life', head: 'In service', cell: (c: { lifecycle: string }) =>
                        <Pill tone={c.lifecycle === 'active' ? 'good' : 'danger'}>{words(c.lifecycle)}</Pill> },
                    { key: 'pick', head: '', cell: (c: { cylinderId: number }) =>
                        <Button small kind="primary" onClick={() => { void identify(c.cylinderId); }} disabled={runner.busy}>This is the one</Button> },
                  ] as readonly Column<(typeof candidates)[number]>[]}
                  rows={candidates} empty="none" />
              </>)}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A SETTLED INTAKE'S HISTORY, AND THE LATE-ENTRY DOOR (0095).
 *
 * Once a manager has decided, the intake stops being a worklist item and becomes
 * history — which still has to be readable, because the yard's account of what it found
 * is evidence about a cylinder somebody now owns. A safety action done BEFORE that
 * decision can still be written up here: refusing it would not un-reduce the pressure,
 * it would only lose the account, and teach the yard that telling the truth late is
 * impossible. It is labelled as entered after resolution for ever.
 */
function SettledIntakeHistoryRow({ row, session, onDone }: {
  row: Views['customerJobs.cylinders']['result'][number]; session: Session; onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [kind, setKind] = useState('pressure_reduced');
  const [reason, setReason] = useState('');
  const [why, setWhy] = useState('');
  const [when, setWhen] = useState('');
  const settledAs = row.contestedIntakeStatus === 'same_cylinder' ? 'bound to an existing cylinder'
    : row.contestedIntakeStatus === 'duplicate_stamping' ? 'approved as a genuine duplicate stamping'
    : row.contestedIntakeStatus === 'intake_corrected' ? 'corrected' : String(row.contestedIntakeStatus);

  async function recordLate() {
    const ok = await runner.run((retryKey) => api.command('job.recordLateIntakeSafetyAction', {
      contestedIntakeId: row.contestedIntakeId!, kind, actedAt: new Date(when).toISOString(),
      reason: reason.trim(), lateEntryReason: why.trim(),
    } as never, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setReason(''); setWhy(''); setWhen(''); onDone(); }
  }

  return (
    <div id={`settled-${row.jobCylinderId}`} style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title={`${row.contestedIntakeNumber} was settled — ${settledAs}.`}
              detail="The intake's original entry, its corrections, the candidates it froze, the evidence and the safety work all remain readable against it. Nothing was rewritten." />
      {can(session, 'job.record_late_intake_safety_action')
        ? <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
            <Select name={`late-kind-${row.jobCylinderId}`} label="Safety action done before the decision" value={kind} onChange={setKind}
                    options={[{ value: 'isolated', label: 'Isolated it' },
                              { value: 'pressure_reduced', label: 'Reduced the pressure' },
                              { value: 'gas_evacuated', label: 'Evacuated the gas safely' },
                              { value: 'gas_recovered', label: 'Recovered the gas' },
                              { value: 'valve_leak_checked', label: 'Checked the valve — it was leaking' }]} />
            <Text name={`late-when-${row.jobCylinderId}`} label="When it happened" value={when} onChange={setWhen} />
            <Text name={`late-reason-${row.jobCylinderId}`} label="Why it was done" value={reason} onChange={setReason} />
            <Text name={`late-why-${row.jobCylinderId}`} label="Why it is being entered late" value={why} onChange={setWhy} />
            <Button kind="primary" disabled={runner.busy || !reason.trim() || !why.trim() || !when.trim()}
                    onClick={recordLate}>Record late safety action</Button>
          </div>
        : null}
    </div>
  );
}

/**
 * SAFETY WORK ON A VESSEL NOBODY HAS IDENTIFIED (0094).
 *
 * Shown to the yard as well as to the manager, because the person standing next to a
 * leaking valve is the one who reduces it. Nothing here can make the cylinder fit to
 * fill — the record has no contents, safety-release, test or repair field at all — and
 * the copy says so, so nobody mistakes "gas recovered" for "verified empty".
 */
function SafetyActionRow({ row, session, kind, setKind, reason, setReason, note, setNote, pressure, setPressure, onDone }: {
  row: Views['customerJobs.cylinders']['result'][number];
  session: Session;
  kind: string; setKind: (v: string) => void;
  reason: string; setReason: (v: string) => void;
  note: string; setNote: (v: string) => void;
  pressure: string; setPressure: (v: string) => void;
  onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  if (!can(session, 'job.record_intake_safety_action')) return null;
  async function record() {
    const body: Record<string, unknown> = {
      contestedIntakeId: row.contestedIntakeId!, kind, actedAt: new Date().toISOString(),
      reason: reason.trim(), ...(note.trim() ? { notes: note.trim() } : {}),
      ...(kind === 'pressure_read' && pressure.trim()
        ? { pressureValue: Number(pressure), pressureUnit: 'bar' } : {}),
      ...(kind === 'valve_leak_checked' ? { valveLeaking: true } : {}),
      ...(kind === 'condition_observed' ? { visibleCondition: note.trim() || 'as received' } : {}),
      ...(kind === 'suspected_contents' ? { suspectedContents: note.trim() || 'unknown' } : {}),
    };
    const ok = await runner.run((retryKey) => api.command('job.recordIntakeSafetyAction', body as never, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setReason(''); setNote(''); setPressure(''); onDone(); }
  }
  return (
    <div id={`safety-${row.jobCylinderId}`} style={{ marginTop: 10, borderTop: '1px dashed var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="Safety work is allowed while the identity is unsettled."
              detail="Record what you saw and what you did to make it safe. This does NOT make the cylinder empty, repaired, tested or fit to fill — emptying is not verifying, and recovering gas is not a repair." />
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Select name={`safety-kind-${row.jobCylinderId}`} label="What happened" value={kind} onChange={setKind}
                options={[{ value: 'condition_observed', label: 'Looked at it — visible condition' },
                          { value: 'valve_leak_checked', label: 'Checked the valve — it is leaking' },
                          { value: 'pressure_read', label: 'Read the pressure' },
                          { value: 'suspected_contents', label: 'Noted what it seems to contain' },
                          { value: 'isolated', label: 'Isolated it' },
                          { value: 'pressure_reduced', label: 'Reduced the pressure' },
                          { value: 'gas_evacuated', label: 'Evacuated the gas safely' },
                          { value: 'gas_recovered', label: 'Recovered the gas' }]} />
        {kind === 'pressure_read'
          ? <Text name={`safety-bar-${row.jobCylinderId}`} label="Pressure (bar)" value={pressure} onChange={setPressure} />
          : null}
        <Text name={`safety-note-${row.jobCylinderId}`} label="What you saw" value={note} onChange={setNote} />
        <Text name={`safety-reason-${row.jobCylinderId}`} label="Why" value={reason} onChange={setReason} />
        <Button kind="primary" disabled={runner.busy || !reason.trim()} onClick={record}>Record safety action</Button>

      </div>
    </div>
  );
}

/**
 * A CONTESTED INTAKE UNDER PHYSICAL ISOLATION (0091/0092, owner's ruling).
 *
 * A line whose stamping named more than one cylinder has NO cylinder bound to it.
 * Without this block that row reads as an empty line somebody forgot to complete —
 * which is exactly how a cylinder of unknown identity gets quietly worked on. So it
 * announces itself in the words the yard needs: what is isolated, under which CI
 * number, why the register could not say which vessel it is, and that filling and
 * dispatch are refused until a branch manager settles it.
 *
 * An ordinary user sees the same facts and no controls. That is deliberate: the
 * person at the counter must be able to READ the situation and tell the driver, and
 * must not be able to resolve it.
 */
function ContestedIntakeRow({ row, session, onDone }: {
  row: Views['customerJobs.cylinders']['result'][number]; session: Session; onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [outcome, setOutcome] = useState('reuse');
  const [reason, setReason] = useState('');
  const [cylinderId, setCylinderId] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [cylinderType, setCylinderType] = useState('');
  const [gasService, setGasService] = useState('');
  const [capacityQty, setCapacityQty] = useState('');
  const [capacityUnit, setCapacityUnit] = useState('L');
  const [markings, setMarkings] = useState('');
  // 0104, the owner's ruling of 29 Sep — photographs support this decision, they do not
  // make it. A branch manager states where the two vessels were seen together and what
  // was found to differ between them.
  const [comparedAt, setComparedAt] = useState('');
  const [differences, setDifferences] = useState('');
  // 0094 — the yard's own record of making an isolated vessel safe. Available to
  // whoever is standing next to it, not only to the manager, and it changes no
  // contents, safety-release, test or repair fact.
  const [safetyKind, setSafetyKind] = useState('condition_observed');
  const [safetyReason, setSafetyReason] = useState('');
  const [safetyNote, setSafetyNote] = useState('');
  const [pressure, setPressure] = useState('');

  const heading = `Contested intake under physical isolation — ${row.contestedIntakeNumber ?? ''}`;
  const detail = `${row.contestedConflictReason ?? ''} The physical cylinder has been taken in and isolated; it is NOT recorded as any of the `
    + `${row.contestedCandidateCount ?? 0} cylinder(s) it might be. Filling, QC release, Customer Fill Dispatch, transfer and condemnation are refused `
    + `until a branch manager settles which cylinder this is. Safety inspection, isolation and emergency gas handling remain available.`;

  if (!can(session, 'job.resolve_contested_intake')) {
    return (
      <div id={`contested-${row.jobCylinderId}`} style={{ marginTop: 10 }}>
        <Banner kind="warn" title={heading} detail={`${detail} A branch manager has been notified.`} />
        <SafetyActionRow row={row} session={session} kind={safetyKind} setKind={setSafetyKind}
                         reason={safetyReason} setReason={setSafetyReason}
                         note={safetyNote} setNote={setSafetyNote}
                         pressure={pressure} setPressure={setPressure} onDone={onDone} />
      </div>
    );
  }

  async function resolve() {
    const body: Record<string, unknown> = {
      contestedIntakeId: row.contestedIntakeId!, outcome, reason: reason.trim(),
    };
    if (outcome === 'reuse') body['cylinderId'] = Number(cylinderId);
    if (outcome === 'correct') body['correction'] = { serialNo: serialNo.trim(), customerId: Number(customerId) };
    if (outcome === 'duplicate') {
      body['duplicate'] = {
        manufacturer: manufacturer.trim(), cylinderType: cylinderType.trim(), gasService: gasService.trim(),
        capacityQty: Number(capacityQty), capacityUnit: capacityUnit.trim(), markings: markings.trim(),
        comparedInPersonAt: comparedAt.trim(), differencesFound: differences.trim(),
      };
    }
    const ok = await runner.run((retryKey) => api.command('job.resolveContestedIntake', body as never, retryKey ? { idempotencyKey: retryKey } : {}));
    // Refetch rather than trust the success message: what matters is the
    // persisted state, and a toast is not evidence of it.
    if (ok) { setReason(''); onDone(); }
  }

  return (
    <div id={`contested-${row.jobCylinderId}`} style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="warn" title={heading} detail={detail} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
        <Select name={`ci-outcome-${row.jobCylinderId}`} label="What did you find" value={outcome} onChange={setOutcome}
                options={[{ value: 'reuse', label: 'It is one of these cylinders — bind it to that record' },
                          { value: 'duplicate', label: 'It is a SECOND cylinder stamped the same — approve a duplicate stamping' },
                          { value: 'correct', label: 'The intake was keyed wrongly — append a correction' }]} />
        {outcome === 'reuse'
          ? <Text name={`ci-cyl-${row.jobCylinderId}`} label="Cylinder id it actually is" value={cylinderId} onChange={setCylinderId} />
          : null}
        {outcome === 'correct'
          ? <>
              <Text name={`ci-serial-${row.jobCylinderId}`} label="Corrected stamped serial" value={serialNo} onChange={setSerialNo} />
              <Text name={`ci-cust-${row.jobCylinderId}`} label="Corrected customer id" value={customerId} onChange={setCustomerId} />
            </>
          : null}
        {outcome === 'duplicate'
          ? <>
              <Text name={`ci-make-${row.jobCylinderId}`} label="Manufacturer" value={manufacturer} onChange={setManufacturer} />
              <Text name={`ci-type-${row.jobCylinderId}`} label="Cylinder type" value={cylinderType} onChange={setCylinderType} />
              <Text name={`ci-gas-${row.jobCylinderId}`} label="Gas service" value={gasService} onChange={setGasService} />
              <Text name={`ci-cap-${row.jobCylinderId}`} label="Capacity" value={capacityQty} onChange={setCapacityQty} />
              <Text name={`ci-capu-${row.jobCylinderId}`} label="Capacity unit" value={capacityUnit} onChange={setCapacityUnit} />
              <Text name={`ci-marks-${row.jobCylinderId}`} label="Other markings" value={markings} onChange={setMarkings} />
              <Text name={`ci-where-${row.jobCylinderId}`} label="Where you compared the two, in person" value={comparedAt} onChange={setComparedAt} />
              <Text name={`ci-differs-${row.jobCylinderId}`} label="What differs between them" value={differences} onChange={setDifferences} />
            </>
          : null}
        <Text name={`ci-reason-${row.jobCylinderId}`} label="Why" value={reason} onChange={setReason} />
        <Button kind="primary" disabled={runner.busy || !reason.trim()
                  || (outcome === 'reuse' && !cylinderId.trim())
                  || (outcome === 'correct' && (!serialNo.trim() || !customerId.trim()))
                  || (outcome === 'duplicate' && (!manufacturer.trim() || !cylinderType.trim() || !gasService.trim()
                       || !capacityQty.trim() || !markings.trim() || !row.contestedEvidenceComplete
                       || comparedAt.trim().length < 3 || differences.trim().length < 10))}
                onClick={resolve}>Settle identity</Button>
      </div>
      <SafetyActionRow row={row} session={session} kind={safetyKind} setKind={setSafetyKind}
                       reason={safetyReason} setReason={setSafetyReason}
                       note={safetyNote} setNote={setSafetyNote}
                       pressure={pressure} setPressure={setPressure} onDone={onDone} />
      {outcome === 'duplicate'
        ? <Banner kind={row.contestedEvidenceComplete ? 'info' : 'warn'}
                  title={row.contestedEvidenceComplete
                    ? 'Evidence is complete: both cylinders and both stampings are photographed.'
                    : 'Photographs are required before a second identity can be created.'}
                  detail={`Deciding that these are TWO physical vessels cannot rest on a picture of one. `
                    + `This intake has ${row.contestedCylinderPhotos} cylinder photograph(s) and ${row.contestedStampPhotos} stamp photograph(s); `
                    + `two of each are required — and they are only half of it. You must also have compared the two vessels IN PERSON and said `
                    + `what differs between them. If nothing does, this is not a duplicate stamping: it is one cylinder recorded twice, and you `
                    + `are the only person holding both.`} />
        : null}
    </div>
  );
}

/**
 * A DISPUTED ARRIVAL, RESOLVED BY THE PERSON WHOSE JOB IT IS (26 Sep).
 *
 * A cylinder received under an ownership dispute is quarantined — which on a
 * screen looks exactly like a valve leak. The supervisor could not see that
 * there was anything to settle, and had no way to settle it but the command
 * door. Both resolutions are offered here, in the words of what each one does:
 * the job named the wrong customer (the work is corrected to the true owner and
 * the registry is not touched), or the cylinder really did change hands (a
 * documented transfer of ownership). Either way a reason is required.
 */
function ResolveDisputeRow({ jobId, row, session, onDone }: {
  jobId: number; row: Views['customerJobs.cylinders']['result'][number]; session: Session; onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [reason, setReason] = useState('');
  const [resolution, setResolution] = useState('correct_job');
  if (!can(session, 'job.resolve_ownership_dispute')) {
    return (
      <Banner kind="warn" title={`Cylinder ${row.serialNo ?? row.cylinderId} is held: its ownership is in dispute.`}
              detail="Resolving it needs a supervisor. Until then it stays quarantined and cannot be sent anywhere." />
    );
  }
  async function resolve() {
    const ok = await runner.run((retryKey) => api.command('job.resolveOwnershipDispute', {
      jobId, cylinderId: row.cylinderId!, resolution: resolution as 'correct_job' | 'transfer_ownership',
      ...(resolution === 'transfer_ownership' && row.registeredOwnerCustomerId !== null ? {} : {}),
      reason: reason.trim(),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setReason(''); onDone(); }
  }
  return (
    <div id={`dispute-${row.cylinderId}`} style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="warn" title={`Ownership is in dispute for ${row.serialNo ?? `cylinder ${row.cylinderId}`}.`}
              detail={`The register says this cylinder belongs to ${row.registeredOwnerName ?? 'another owner'}, and it arrived on this customer's job. It is quarantined until somebody settles that. ${row.disputeReason ?? ''}`} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Select name={`dispute-how-${row.cylinderId}`} label="What actually happened" value={resolution} onChange={setResolution}
                options={[{ value: 'correct_job', label: 'The job named the wrong customer — the work belongs to the registered owner' },
                          { value: 'transfer_ownership', label: 'The cylinder changed hands — transfer it to this customer' }]} />
        <Text name={`dispute-reason-${row.cylinderId}`} label="Why" value={reason} onChange={setReason}
              help="kept with the resolution — a cylinder that changed owner is always asked about later" />
        <Button small kind="primary" onClick={() => { void resolve(); }} disabled={runner.busy || !reason.trim()}>
          {runner.busy ? 'Settling…' : 'Settle it'}
        </Button>
      </div>
      {row.correctedCustomerName
        ? <p className="muted">Settled: the work on this cylinder belongs to {row.correctedCustomerName} — {row.correctedReason}</p>
        : null}
    </div>
  );
}

/** One cylinder's inspection: a pressure reading and an outcome — released, quarantined, or still awaiting. */
function InspectRow({ jobId, cylinderId, onDone }: { jobId: number; cylinderId: number; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [pressure, setPressure] = useState('');
  const [note, setNote] = useState('');

  async function record(outcome: 'awaiting_inspection' | 'released' | 'quarantined') {
    const ok = await runner.run((retryKey) => api.command('job.inspect', {
      jobId, cylinderId, outcome,
      ...(pressure.trim() ? { pressureBar: Number(pressure) } : {}),
      ...(note.trim() ? { valveCondition: note.trim() } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setPressure(''); setNote(''); onDone(); }
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <span>Cylinder #{cylinderId}</span>
      <Text name={`inspect-pressure-${cylinderId}`} label="Pressure (bar)" value={pressure} onChange={setPressure} />
      <Text name={`inspect-note-${cylinderId}`} label="Condition note" value={note} onChange={setNote} />
      <Button small kind="primary" onClick={() => { void record('released'); }} disabled={runner.busy}>Release</Button>
      <Button small kind="danger" onClick={() => { void record('quarantined'); }} disabled={runner.busy}>Quarantine</Button>
    </div>
  );
}

/** WHAT THE PAGE SHOWED BEFORE THE TABS: details, agreed rates, sites, registrations, addresses, contacts, site terms, history. */
function RecordTab({ customerId, session, d, reload, onReadiness, readiness }: {
  customerId: number; session: Session;
  d: Views['customers.get']['result']; reload: () => void; onReadiness: () => void; readiness: Readiness | null;
}) {
  const sites = useAsk(() => api.view('sites.list', { customerId }), [customerId], MESSAGES);
  const notReady = !!readiness && !readiness.ready;
  const [addingSite, setAddingSite] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [pricing, setPricing] = useState(false);
  const [editingSite, setEditingSite] = useState<number | null>(null);

  return (
    <>
      <div className="toolbar">
        {can(session, 'customer.manage')
          ? <Button onClick={() => { setCorrecting((v) => !v); setPricing(false); }}>{correcting ? 'Leave it as it is' : 'Correct the details'}</Button> : null}
        {can(session, 'customer.manage')
          ? <Button kind={notReady ? 'ghost' : 'primary'} onClick={() => { setPricing((v) => !v); setCorrecting(false); }}>{pricing ? 'Leave the rates alone' : d.terms.missing ? 'Enter the agreed rates' : 'Change the agreed rates'}</Button> : null}
      </div>

      {correcting ? <CorrectCustomer customer={d.customer} onDone={() => { setCorrecting(false); reload(); }} /> : null}
      {pricing ? <SetTerms customerId={customerId} terms={d.terms} onDone={() => { setPricing(false); reload(); }} /> : null}
      <div className="grid two">
        <Card title="The customer">
          <div className="detail-grid">
            <div><div className="k">Status</div><div className="v"><Pill tone={d.customer.active ? 'good' : 'muted'}>{d.customer.active ? 'active' : 'closed'}</Pill></div></div>
            <div><div className="k">On the books since</div><div className="v">{date(d.customer.createdAt)}</div></div>
            <div><div className="k">Sites</div><div className="v">{count(d.sites.length)}</div></div>
          </div>
        </Card>
        <Card title="Agreed rates" sub="what a dispatch and a holding are priced from">
          {d.terms.missing ? (
            <Banner kind="warn" title="This customer has no agreed rates."
                    detail="A manager must enter them before an order can be approved or a load priced." />
          ) : (
            <div className="detail-grid">
              <div><div className="k">Effective from</div><div className="v">{date(d.terms.effectiveFrom)}</div></div>
              <div><div className="k">Freight</div><div className="v"><Money paise={d.terms.freightPaise} /></div></div>
              <div><div className="k">Loading / unloading</div><div className="v"><Money paise={d.terms.loadingPaisePerCyl} /> / <Money paise={d.terms.unloadingPaisePerCyl} /></div></div>
              <div><div className="k">Gases priced</div><div className="v">{Object.keys((d.terms.gasRates ?? {}) as Record<string, unknown>).join(', ') || '—'}</div></div>
              {d.terms.lapsed ? <div><div className="k">Lapsed</div><div className="v"><Pill tone="danger">{`lapsed ${date(d.terms.lapsedOn)}`}</Pill></div></div> : null}
            </div>
          )}
        </Card>
      </div>

      <Card className="customer-sites" title="Sites" sub="where the cylinders are delivered"
            actions={can(session, 'site.manage') ? <Button small onClick={() => setAddingSite((v) => !v)}>{addingSite ? 'Cancel' : 'Add a site'}</Button> : null}>
        {addingSite ? <NewSite customerId={customerId} onDone={() => { setAddingSite(false); sites.reload(); reload(); }} /> : null}
        {sites.loading ? <Loading what="sites" /> : (
          <Register
            columns={[
              { key: 'name', head: 'Site', cell: (r: { name: string; isPrimary: boolean }) => <>{r.name} {r.isPrimary ? <Pill tone="primary">primary</Pill> : null}</> },
              { key: 'address', head: 'Address', cell: (r: { address: string | null }) => r.address ?? <span className="muted">—</span> },
              { key: 'active', head: 'Status', cell: (r: { active: boolean }) => <Pill tone={r.active ? 'good' : 'muted'}>{r.active ? 'open' : 'closed'}</Pill> },
              { key: 'edit', head: '', cell: (r: { siteId: number }) => can(session, 'site.manage')
                  ? <Button small onClick={() => setEditingSite((v) => (v === r.siteId ? null : r.siteId))}>{editingSite === r.siteId ? 'Close' : 'Correct'}</Button>
                  : null },
            ]}
            rows={sites.data?.rows ?? []} empty="This customer has no sites yet." />
        )}
        {editingSite !== null && sites.data
          ? <CorrectSite site={sites.data.rows.find((r) => r.siteId === editingSite)!}
                         onDone={() => { setEditingSite(null); sites.reload(); reload(); }} />
          : null}
      </Card>

      <SiteTermsPanel sites={(sites.data?.rows ?? []).filter((r) => r.active)} session={session} />

      <PartyPanel customerId={customerId} session={session} onChanged={onReadiness} />

      <History customerId={customerId} />
    </>
  );
}

/**
 * THE PARTY'S OWN RECORD (audit A-row; the last of the fifteen absent):
 * registrations, addresses and contacts are DATED FACTS — recorded and
 * closed, corrected with a reason, never silently edited.
 */
function PartyPanel({ customerId, session, onChanged }: { customerId: number; session: Session; onChanged?: () => void }) {
  const asked0 = useAsk(() => api.view('customers.party', { customerId }), [customerId], MESSAGES);
  // the readiness card above reads the same facts: it must not go stale when a registration is closed or a contact added (audit C1, 22 Sep)
  const asked = { ...asked0, reload: () => { asked0.reload(); onChanged?.(); } };
  const runner = useRun(MESSAGES);
  const may = can(session, 'customer.manage');
  const [regGstin, setRegGstin] = useState(''); const [regName, setRegName] = useState(''); const [regFrom, setRegFrom] = useState(today());
  const [closing, setClosing] = useState<Record<number, string>>({});
  const [conName, setConName] = useState(''); const [conPhone, setConPhone] = useState('');
  const [addrKind, setAddrKind] = useState('billing'); const [addrLine1, setAddrLine1] = useState('');
  const [addrCity, setAddrCity] = useState(''); const [addrPin, setAddrPin] = useState('');

  if (asked.loading) return <Card title="The party's record"><Loading what="registrations, addresses and contacts" /></Card>;
  if (asked.error) return <Card title="The party's record"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;

  async function recordRegistration() {
    const ok = await runner.run(() => api.command('customer.recordRegistration',
      { customerId, gstin: regGstin.trim(), legalName: regName.trim(), effectiveFrom: regFrom }));
    if (ok) { setRegGstin(''); setRegName(''); asked.reload(); }
  }
  async function closeRegistration(registrationId: number) {
    const reason = (closing[registrationId] ?? '').trim();
    if (!reason) return;
    const ok = await runner.run(() => api.command('customer.closeRegistration', { registrationId, effectiveTo: today(), reason }));
    if (ok) asked.reload();
  }
  async function addContact() {
    const ok = await runner.run(() => api.command('customer.addContact',
      { customerId, name: conName.trim(), ...(conPhone.trim() ? { phone: conPhone.trim() } : {}), isPrimary: d.contacts.length === 0 }));
    if (ok) { setConName(''); setConPhone(''); asked.reload(); }
  }
  async function makePrimary(contactId: number) {
    const ok = await runner.run(() => api.command('customer.updateContact',
      { contactId, changes: { isPrimary: true }, reason: 'made the primary contact from the party record' }));
    if (ok) asked.reload();
  }
  async function setAddress() {
    const ok = await runner.run(() => api.command('customer.setAddress', {
      customerId, kind: addrKind as 'registered' | 'billing' | 'delivery', line1: addrLine1.trim(),
      ...(addrCity.trim() ? { city: addrCity.trim() } : {}), ...(addrPin.trim() ? { pincode: addrPin.trim() } : {}),
    }));
    if (ok) { setAddrLine1(''); setAddrCity(''); setAddrPin(''); asked.reload(); }
  }

  return (
    <Card title="Registration, addresses and contacts" sub="each is recorded with its dates and closed when it ends, never edited">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}

      <h3 className="section">GST registrations</h3>
      <Register
        columns={[
          { key: 'gstin', head: 'GSTIN', cell: (r: { gstin: string }) => r.gstin || '—' },
          { key: 'legal', head: 'Legal name', cell: (r: { legalName: string }) => r.legalName },
          { key: 'from', head: 'From', cell: (r: { effectiveFrom: string }) => date(r.effectiveFrom) },
          { key: 'to', head: 'To', cell: (r: { effectiveTo: string | null }) => r.effectiveTo ? date(r.effectiveTo) : <Pill tone="good">in force</Pill> },
          { key: 'close', head: '', cell: (r: { registrationId: number; effectiveTo: string | null }) => may && !r.effectiveTo
              ? <span style={{ display: 'flex', gap: 6 }}>
                  <Text name={`close-reg-${r.registrationId}`} label="" value={closing[r.registrationId] ?? ''}
                        onChange={(v) => setClosing((old) => ({ ...old, [r.registrationId]: v }))} />
                  <Button small kind="danger" onClick={() => { void closeRegistration(r.registrationId); }}
                          disabled={runner.busy || !(closing[r.registrationId] ?? '').trim()}>Close</Button>
                </span> : null },
        ]}
        rows={d.registrations} empty="No registration recorded — the paper header falls back to the customer's GSTIN field." />
      {may ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
          <Text name="reg-gstin" label="GSTIN" value={regGstin} onChange={setRegGstin} />
          <Text name="reg-name" label="Legal name" value={regName} onChange={setRegName} />
          <Text name="reg-from" label="Effective from" type="date" value={regFrom} onChange={setRegFrom} />
          <Button small onClick={() => { void recordRegistration(); }} disabled={runner.busy || !regName.trim()}>Record it</Button>
        </div>
      ) : null}

      <h3 className="section">Addresses</h3>
      <Register
        columns={[
          { key: 'kind', head: 'Kind', cell: (a: { kind: string }) => <Pill>{a.kind}</Pill> },
          { key: 'line', head: 'Address', cell: (a: { line1: string; city: string | null; pincode: string | null }) =>
              `${a.line1}${a.city ? `, ${a.city}` : ''}${a.pincode ? ` – ${a.pincode}` : ''}` },
          { key: 'active', head: '', cell: (a: { active: boolean }) => a.active ? null : <Pill tone="muted">superseded</Pill> },
        ]}
        rows={d.addresses} empty="No structured address on file — the sites carry their own delivery addresses." />
      {may ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
          <Select name="addr-kind" label="Kind" value={addrKind} onChange={setAddrKind}
                  options={[{ value: 'registered', label: 'Registered' }, { value: 'billing', label: 'Billing' }, { value: 'delivery', label: 'Delivery' }]} />
          <Text name="addr-line1" label="Line 1" value={addrLine1} onChange={setAddrLine1} />
          <Text name="addr-city" label="City" value={addrCity} onChange={setAddrCity} />
          <Text name="addr-pin" label="PIN" value={addrPin} onChange={setAddrPin} />
          <Button small onClick={() => { void setAddress(); }} disabled={runner.busy || !addrLine1.trim()}>Set the address</Button>
        </div>
      ) : null}

      <h3 className="section">Contacts</h3>
      <Register
        columns={[
          { key: 'name', head: 'Name', cell: (c: { name: string; isPrimary: boolean }) => <>{c.name} {c.isPrimary ? <Pill tone="primary">primary</Pill> : null}</> },
          { key: 'role', head: 'Role', cell: (c: { role: string | null }) => c.role ?? '—' },
          { key: 'phone', head: 'Phone', cell: (c: { phone: string | null }) => c.phone ?? '—' },
          { key: 'primary', head: '', cell: (c: { contactId: number; isPrimary: boolean; active: boolean }) => may && !c.isPrimary && c.active
              ? <Button small onClick={() => { void makePrimary(c.contactId); }} disabled={runner.busy}>Make primary</Button> : null },
        ]}
        rows={d.contacts} empty="Nobody is on file to call." />
      {may ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
          <Text name="con-name" label="Name" value={conName} onChange={setConName} />
          <Text name="con-phone" label="Phone" value={conPhone} onChange={setConPhone} />
          <Button small onClick={() => { void addContact(); }} disabled={runner.busy || !conName.trim()}>Add the contact</Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * WHAT EACH SITE IS CHARGED (0045) — the customer's card unless a dated
 * override says otherwise; ending an override is dated too, never a delete.
 */
function SiteTermsPanel({ sites, session }: { sites: { siteId: number; name: string }[]; session: Session }) {
  const [siteId, setSiteId] = useState('');
  const runner = useRun(MESSAGES);
  const asked = useAsk(() => (siteId ? api.view('sites.terms', { siteId: Number(siteId) }) : Promise.resolve(null)), [siteId], MESSAGES);
  const [freight, setFreight] = useState('');
  const [from, setFrom] = useState(today());
  const [endReason, setEndReason] = useState('');
  const may = can(session, 'site.terms');

  async function setOverride() {
    const ok = await runner.run((retryKey) => api.command('site.setTerms', {
      siteId: Number(siteId), effectiveFrom: from, freightPaise: Math.round(Number(freight) * 100),
      reason: 'freight override set from the site terms panel',
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setFreight(''); asked.reload(); }
  }
  async function endOverride() {
    const t = asked.data;
    if (!t?.overrideId || !endReason.trim()) return;
    const overrideId = t.overrideId;
    const ok = await runner.run(() => api.command('site.endTerms', { overrideId, effectiveTo: today(), reason: endReason.trim() }));
    if (ok) { setEndReason(''); asked.reload(); }
  }

  const t = asked.data;
  return (
    <Card title="What each site is charged" sub="the customer's agreed rates, unless this site has special rates">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Select name="terms-site" label="Site" value={siteId} onChange={setSiteId} placeholder="Choose a site"
              options={sites.map((st) => ({ value: String(st.siteId), label: st.name }))} />
      {t ? (
        <>
          <div className="detail-grid" style={{ marginTop: 10 }}>
            <div><div className="k">Freight</div><div className="v">{t.freightPaise === null ? '—' : <Money paise={t.freightPaise} />}{t.overridden.freight ? <> <Pill tone="testing">special rate</Pill></> : <span className="muted"> (customer card)</span>}</div></div>
            <div><div className="k">Loading / cyl</div><div className="v">{t.loadingPaisePerCyl === null ? '—' : <Money paise={t.loadingPaisePerCyl} />}{t.overridden.loading ? <> <Pill tone="testing">special rate</Pill></> : null}</div></div>
            <div><div className="k">Override window</div><div className="v">{t.overrideId ? `${date(t.effectiveFrom ?? '')} – ${t.effectiveTo ? date(t.effectiveTo) : 'open'}` : 'none — the customer card applies whole'}</div></div>
          </div>
          {may ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10 }}>
              <Text name="terms-freight" label="Freight for THIS site (₹)" value={freight} onChange={setFreight} />
              <Text name="terms-from" label="From" type="date" value={from} onChange={setFrom} />
              <Button small onClick={() => { void setOverride(); }} disabled={runner.busy || !freight.trim()}>Set the override</Button>
              {t.overrideId ? (
                <>
                  <Text name="terms-end-reason" label="Why it ends" value={endReason} onChange={setEndReason} />
                  <Button small kind="danger" onClick={() => { void endOverride(); }} disabled={runner.busy || !endReason.trim()}>End the override</Button>
                </>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

/**
 * WHAT HAPPENED TO THIS CUSTOMER, AND WHY. The corrections above ask for a
 * reason; this is where the reason is read back. Without it the reason is a
 * field nobody ever sees, and asking for it is then only a nuisance.
 */
function History({ customerId }: { customerId: number }) {
  const asked = useAsk(() => api.view('audit.entity', { entity: `customers.customers:${customerId}`, pageSize: 20 }), [customerId], MESSAGES);
  if (asked.loading) return <Card title="What happened to it"><Loading what="the history" /></Card>;
  if (asked.error) return <Card title="What happened to it"><Banner kind="warn" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const rows = asked.data!.rows;
  return (
    <Card title="What happened to it" sub="who changed what, when, and why">
      {rows.length === 0 ? <p className="muted">Nothing has been changed since this customer was put on the books.</p> : (
        <ul className="timeline">
          {rows.map((a) => (
            <li key={a.auditId}>
              <strong>{actionWords(a.action)}</strong> by {a.actorName ?? `user ${a.actorId}`}
              {a.reason ? <> — {a.reason}</> : null}
              <div className="when">{when(a.at)}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * CORRECTING WHAT IS ON FILE. A name or a GSTIN is not a typo to be quietly
 * fixed: the challans and memos already issued carry the old one, and somebody
 * will ask why they differ. So the reason is required, and it is kept.
 */
function CorrectCustomer({ customer, onDone }: {
  customer: { customerId: number; name: string; gstin: string | null; state: string | null; active: boolean };
  onDone: () => void;
}) {
  const [name, setName] = useState(customer.name);
  const [gstin, setGstin] = useState(customer.gstin ?? '');
  const [state, setState] = useState(customer.state ?? '');
  const [active, setActive] = useState(customer.active);
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  // Only what actually changed is sent: a command that receives every field
  // cannot tell a correction from a re-save, and the audit trail then says
  // nothing useful.
  const changes = {
    ...(name.trim() !== customer.name ? { name: name.trim() } : {}),
    ...(gstin.trim() !== (customer.gstin ?? '') ? { gstin: gstin.trim() || null } : {}),
    ...(state.trim() !== (customer.state ?? '') ? { state: state.trim() || null } : {}),
    ...(active !== customer.active ? { active } : {}),
  };
  const nothingChanged = Object.keys(changes).length === 0;

  async function submit() {
    const ok = await runner.run(() => api.command('customer.update', { customerId: customer.customerId, changes, reason: reason.trim() }));
    if (ok) onDone();
  }

  return (
    <Card title="Correct the customer" sub="what is already issued keeps the old details — say why this is changing">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="edit-name" label="Customer name" value={name} onChange={setName} required />
          <Text name="edit-gstin" label="GSTIN" value={gstin} onChange={setGstin} help="15 characters, or leave blank" />
          <Text name="edit-state" label="State" value={state} onChange={setState} />
        </div>
        <div>
          <Check name="edit-active" checked={active} onChange={setActive} label="This customer is still on the books" />
          <TextArea name="edit-reason" label="Why it is changing" value={reason} onChange={setReason} help="kept with the change, and readable afterwards" />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim() || nothingChanged}>
        {runner.busy ? 'Saving…' : nothingChanged ? 'Nothing has changed' : 'Save the correction'}
      </Button>
    </Card>
  );
}

function CorrectSite({ site, onDone }: {
  site: { siteId: number; name: string; address: string | null; isPrimary: boolean; active: boolean };
  onDone: () => void;
}) {
  const [name, setName] = useState(site.name);
  const [address, setAddress] = useState(site.address ?? '');
  const [primary, setPrimary] = useState(site.isPrimary);
  const [active, setActive] = useState(site.active);
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  const changes = {
    ...(name.trim() !== site.name ? { name: name.trim() } : {}),
    ...(address.trim() !== (site.address ?? '') ? { address: address.trim() || null } : {}),
    ...(primary !== site.isPrimary ? { isPrimary: primary } : {}),
    ...(active !== site.active ? { active } : {}),
  };
  const nothingChanged = Object.keys(changes).length === 0;

  async function submit() {
    const ok = await runner.run(() => api.command('site.update', { siteId: site.siteId, changes, reason: reason.trim() }));
    if (ok) onDone();
  }

  return (
    <div style={{ marginTop: 16 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Text name="site-edit-name" label="Site name" value={name} onChange={setName} required />
      <TextArea name="site-edit-address" label="Address" value={address} onChange={setAddress} />
      <Check name="site-edit-primary" checked={primary} onChange={setPrimary} label="This is the primary site" />
      <Check name="site-edit-active" checked={active} onChange={setActive} label="Cylinders are still delivered here" />
      <TextArea name="site-edit-reason" label="Why it is changing" value={reason} onChange={setReason} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim() || nothingChanged}>
        {runner.busy ? 'Saving…' : nothingChanged ? 'Nothing has changed' : 'Save the correction'}
      </Button>
    </div>
  );
}

interface RateRow { gas: string; rupees: string }
/** 0082 — a toll-fill rate carries the unit it is per, which is the whole point of it. */
interface JobRateRow { gas: string; rupees: string; unit: 'm3' | 'kg' | 'cylinder' }
interface SlabRow { fromDay: string; toDay: string; rupees: string }

/**
 * THE AGREED RATES. What a dispatch and a holding are priced from, and what an
 * approved order photographs (ADR-012): a change here cannot reprice an order
 * already approved, which is why the effective date is asked for rather than
 * assumed to be today.
 *
 * Money is typed in rupees and sent in paise — the door takes paise and
 * nothing else, so the conversion happens once, here, and a figure that is not
 * money (three decimals, letters) is refused rather than rounded.
 */
function SetTerms({ customerId, terms, onDone }: {
  customerId: number;
  terms: { gasRates: unknown; jobFillRates?: unknown; holdingCard: unknown; freightPaise: number; loadingPaisePerCyl: number; unloadingPaisePerCyl: number; effectiveFrom: string | null; missing: boolean };
  onDone: () => void;
}) {
  const card = (terms.holdingCard ?? {}) as { freeDays?: number; slabs?: { fromDay?: number; toDay?: number; uptoDays?: number; ratePaise?: number }[]; flatRatePaise?: number };
  const asRupees = (paise: number | undefined) => (paise === undefined ? '' : (paise / 100).toFixed(2));

  const [rates, setRates] = useState<RateRow[]>(() => {
    const given = Object.entries((terms.gasRates ?? {}) as Record<string, number>);
    return given.length ? given.map(([gas, paise]) => ({ gas, rupees: asRupees(paise) })) : [{ gas: 'oxygen', rupees: '' }];
  });
  // 0082 — WHAT HE PAYS TO HAVE HIS OWN CYLINDER FILLED. The card has carried
  // these rates since 0082 and nothing could enter them, so a toll-fill job
  // could be received, filled, checked and returned and then never billed.
  // Priced per Nm³ or per kg, because that is how the gas put into it is
  // measured — acetylene is weighed (the owner, 9 Oct).
  const [jobRates, setJobRates] = useState<JobRateRow[]>(() => {
    const given = Object.entries((terms.jobFillRates ?? {}) as Record<string, { ratePaise?: number; unit?: string }>);
    return given.map(([gas, spec]) => ({ gas, rupees: asRupees(spec?.ratePaise), unit: spec?.unit === 'kg' || spec?.unit === 'cylinder' ? spec.unit : 'm3' }));
  });
  const setJobRate = (i: number, patch: Partial<JobRateRow>) =>
    setJobRates((old) => old.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const [freeDays, setFreeDays] = useState(String(card.freeDays ?? 7));
  const [slabs, setSlabs] = useState<SlabRow[]>(() => {
    const given = card.slabs ?? [];
    return given.length
      ? given.map((sl, i) => ({ fromDay: String(sl.fromDay ?? (i === 0 ? 1 : '')), toDay: String(sl.toDay ?? sl.uptoDays ?? ''), rupees: asRupees(sl.ratePaise) }))
      : [{ fromDay: '1', toDay: '30', rupees: '' }];
  });
  const [flat, setFlat] = useState(asRupees(card.flatRatePaise));
  const [freight, setFreight] = useState(asRupees(terms.missing ? undefined : terms.freightPaise));
  const [loading, setLoading] = useState(asRupees(terms.missing ? undefined : terms.loadingPaisePerCyl));
  const [unloading, setUnloading] = useState(asRupees(terms.missing ? undefined : terms.unloadingPaisePerCyl));
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);

  function setRate(i: number, patch: Partial<RateRow>) { setRates((old) => old.map((r, n) => (n === i ? { ...r, ...patch } : r))); }
  function setSlab(i: number, patch: Partial<SlabRow>) { setSlabs((old) => old.map((r, n) => (n === i ? { ...r, ...patch } : r))); }

  const gasRates: Record<string, number> = {};
  let badMoney = false;
  for (const r of rates) {
    if (!r.gas.trim()) continue;
    const paise = rupeesToPaise(r.rupees);
    if (paise === null) { badMoney = true; continue; }
    gasRates[r.gas.trim().toLowerCase()] = paise;
  }
  const slabsOut = slabs.filter((sl) => sl.fromDay.trim() && sl.toDay.trim()).map((sl) => {
    const paise = rupeesToPaise(sl.rupees);
    if (paise === null) badMoney = true;
    return { fromDay: Number(sl.fromDay), toDay: Number(sl.toDay), ratePaise: paise ?? 0 };
  });
  const freightPaise = rupeesToPaise(freight);
  const loadingPaise = rupeesToPaise(loading);
  const unloadingPaise = rupeesToPaise(unloading);
  const flatPaise = flat.trim() ? rupeesToPaise(flat) : undefined;
  const complete = Object.keys(gasRates).length > 0 && slabsOut.length > 0 && !badMoney
    && freightPaise !== null && loadingPaise !== null && unloadingPaise !== null && flatPaise !== null
    && reason.trim().length > 0;

  const jobFillRates: Record<string, { ratePaise: number; unit: 'm3' | 'kg' | 'cylinder' }> = {};
  for (const r of jobRates) {
    const gas = r.gas.trim().toLowerCase();
    const paise = rupeesToPaise(r.rupees);
    if (!gas) continue;
    if (paise === null || paise <= 0) { badMoney = true; continue; }
    jobFillRates[gas] = { ratePaise: paise, unit: r.unit };
  }

  async function submit() {
    const ok = await runner.run(() => api.command('customer.setTerms', {
      customerId, gasRates, jobFillRates,
      holdingCard: { freeDays: Number(freeDays), slabs: slabsOut, ...(flatPaise !== undefined && flatPaise !== null ? { flatRatePaise: flatPaise } : {}) },
      freightPaise: freightPaise!, loadingPaisePerCyl: loadingPaise!, unloadingPaisePerCyl: unloadingPaise!,
      effectiveFrom, reason: reason.trim(),
    }));
    if (ok) onDone();
  }

  return (
    <Card title={terms.missing ? 'Enter the agreed rates' : 'Change the agreed rates'}
          sub="an order already approved keeps the rates it was approved on — this applies from the date below">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {badMoney ? <Banner kind="warn" title="One of the figures is not money." detail="Rupees, with at most two decimals." /> : null}

      <div className="grid two">
        <div>
          <h3 className="section">Gas, per cylinder</h3>
          {rates.map((r, i) => (
            <div className="toolbar" key={i}>
              <div className="field"><label htmlFor={`rate-gas-${i}`}>Gas</label>
                <input id={`rate-gas-${i}`} type="text" value={r.gas} onChange={(e: { target: { value: string } }) => setRate(i, { gas: e.target.value })} /></div>
              <div className="field"><label htmlFor={`rate-rupees-${i}`}>₹ per cylinder</label>
                <input id={`rate-rupees-${i}`} type="text" value={r.rupees} onChange={(e: { target: { value: string } }) => setRate(i, { rupees: e.target.value })} /></div>
              {rates.length > 1 ? <Button small onClick={() => setRates((old) => old.filter((_, n) => n !== i))}>Remove</Button> : null}
            </div>
          ))}
          <Button small onClick={() => setRates((old) => [...old, { gas: '', rupees: '' }])}>Add a gas</Button>

          <h3 className="section">Filling his own cylinders</h3>
          <p className="muted">What he pays for gas put into a cylinder he owns. Per Nm³ for compressed gases, per kg for acetylene — it is weighed.</p>
          {jobRates.map((r, i) => (
            <div className="toolbar" key={`job-${i}`}>
              <div className="field"><label htmlFor={`job-gas-${i}`}>Gas</label>
                <input id={`job-gas-${i}`} type="text" value={r.gas} onChange={(e: { target: { value: string } }) => setJobRate(i, { gas: e.target.value })} /></div>
              <div className="field"><label htmlFor={`job-rupees-${i}`}>₹ per unit</label>
                <input id={`job-rupees-${i}`} type="text" value={r.rupees} onChange={(e: { target: { value: string } }) => setJobRate(i, { rupees: e.target.value })} /></div>
              <div className="field"><label htmlFor={`job-unit-${i}`}>Per</label>
                <select id={`job-unit-${i}`} value={r.unit} onChange={(e: { target: { value: string } }) => setJobRate(i, { unit: e.target.value as JobRateRow['unit'] })}>
                  <option value="m3">Nm³</option><option value="kg">kg</option><option value="cylinder">cylinder</option>
                </select></div>
              <Button small onClick={() => setJobRates((old) => old.filter((_, n) => n !== i))}>Remove</Button>
            </div>
          ))}
          <Button small onClick={() => setJobRates((old) => [...old, { gas: '', rupees: '', unit: 'm3' }])}>Add a filling rate</Button>

          <h3 className="section">Delivery</h3>
          <Text name="freight" label="Freight (₹)" value={freight} onChange={setFreight} required />
          <Text name="loading" label="Loading, per cylinder (₹)" value={loading} onChange={setLoading} required />
          <Text name="unloading" label="Unloading, per cylinder (₹)" value={unloading} onChange={setUnloading} required />
        </div>

        <div>
          <h3 className="section">Holding card</h3>
          <Text name="free-days" label="Free days" type="number" value={freeDays} onChange={setFreeDays} required
                help="days a cylinder may be held before holding is charged" />
          {slabs.map((sl, i) => (
            <div className="toolbar" key={i}>
              <div className="field"><label htmlFor={`slab-from-${i}`}>From day</label>
                <input id={`slab-from-${i}`} type="number" value={sl.fromDay} onChange={(e: { target: { value: string } }) => setSlab(i, { fromDay: e.target.value })} /></div>
              <div className="field"><label htmlFor={`slab-to-${i}`}>To day</label>
                <input id={`slab-to-${i}`} type="number" value={sl.toDay} onChange={(e: { target: { value: string } }) => setSlab(i, { toDay: e.target.value })} /></div>
              <div className="field"><label htmlFor={`slab-rate-${i}`}>₹ per day</label>
                <input id={`slab-rate-${i}`} type="text" value={sl.rupees} onChange={(e: { target: { value: string } }) => setSlab(i, { rupees: e.target.value })} /></div>
              {slabs.length > 1 ? <Button small onClick={() => setSlabs((old) => old.filter((_, n) => n !== i))}>Remove</Button> : null}
            </div>
          ))}
          <Button small onClick={() => setSlabs((old) => [...old, { fromDay: '', toDay: '', rupees: '' }])}>Add a slab</Button>
          <Text name="flat-rate" label="Beyond the last slab, per day (₹)" value={flat} onChange={setFlat} help="leave blank if the last slab runs on" />

          <h3 className="section">When it applies</h3>
          <Text name="effective-from" label="Effective from" type="date" value={effectiveFrom} onChange={setEffectiveFrom} required />
          <TextArea name="terms-reason" label="Reason for the change" value={reason} onChange={setReason} />
        </div>
      </div>

      <Button kind="primary" wide onClick={() => { void submit(); }} disabled={runner.busy || !complete}>
        {runner.busy ? 'Saving the rates…' : complete ? 'Save the agreed rates' : 'Fill in the rates, a slab and a reason'}
      </Button>
      {!terms.missing ? (
        <p className="muted" style={{ textAlign: 'center', marginBottom: 0 }}>
          Currently effective from {date(terms.effectiveFrom)} · freight {money(terms.freightPaise)}
        </p>
      ) : null}
    </Card>
  );
}

function NewSite({ customerId, onDone }: { customerId: number; onDone: () => void }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [primary, setPrimary] = useState(false);
  const runner = useRun(MESSAGES);

  async function submit() {
    const ok = await runner.run(() => api.command('site.create', {
      customerId, name: name.trim(), ...(address.trim() ? { address: address.trim() } : {}), ...(primary ? { isPrimary: true } : {}),
    }));
    if (ok) onDone();
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Text name="site-name" label="Site name" value={name} onChange={setName} required />
      <TextArea name="site-address" label="Address" value={address} onChange={setAddress} />
      <Check name="site-primary" checked={primary} onChange={setPrimary} label="Make this the primary site" />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !name.trim()}>{runner.busy ? 'Saving…' : 'Add the site'}</Button>
    </div>
  );
}

// ============================================================ F07 — onboarding, guided and resumable

export const ONBOARDING_STEPS = [
  { key: 'identity', title: 'Who they are' },
  { key: 'registration', title: 'GST registration' },
  { key: 'site', title: 'Where the cylinders go' },
  { key: 'contacts', title: 'Who to reach' },
  { key: 'terms', title: 'Agreed rates' },
  { key: 'review', title: 'Review' },
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]['key'];

/** The step to resume at: the first fact still missing, or the review when none is. */
export function stepFor(next: ReadinessStep | null): number {
  if (next === null) return 5;
  return ONBOARDING_STEPS.findIndex((st) => st.key === next);
}

export function StepRail({ current }: { current: OnboardingStep }) {
  return (
    <ol className="segmented" style={{ listStyle: 'none', padding: 0, display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 14px' }}>
      {ONBOARDING_STEPS.map((st, i) => (
        <li key={st.key} className={`seg-btn${st.key === current ? ' on' : ''}`} aria-current={st.key === current ? 'step' : undefined}
            style={{ padding: '6px 10px' }}>{i + 1}. {st.title}</li>
      ))}
    </ol>
  );
}

/** The review: each fact, what is recorded for it, and which actions are still blocked and why. */
export function ReviewPanel({ readiness }: { readiness: Readiness }) {
  const actions: { key: keyof Readiness['blocks']; label: string }[] = [
    { key: 'quote', label: 'Quoting' }, { key: 'order', label: 'Ordering' }, { key: 'dispatch', label: 'Dispatch' }, { key: 'message', label: 'Messaging' },
  ];
  return (
    <>
      {readiness.ready
        ? <Banner kind="good" title="Ready to trade." detail="Every fact an offer, an order, a load and a message need is on record." />
        : <Banner kind="warn" title="Not ready yet." detail="What is recorded stays recorded; the missing facts below are all that stand between this customer and trading." />}
      <Register
        columns={[
          { key: 'fact', head: 'Fact', cell: (i: { title: string }) => i.title },
          { key: 'state', head: 'Recorded?', cell: (i: { ok: boolean }) => <Pill tone={i.ok ? 'good' : 'danger'}>{i.ok ? 'recorded' : 'missing'}</Pill> },
          { key: 'detail', head: 'What is on record', cell: (i: { detail: string }) => i.detail },
        ]}
        rows={readiness.items} empty="nothing to review" />
      <h3 className="section">What this customer can do</h3>
      <div className="detail-grid">
        {actions.map((a) => (
          <div key={a.key}><div className="k">{a.label}</div>
            <div className="v">{readiness.blocks[a.key] === null ? <Pill tone="good">possible</Pill> : <><Pill tone="danger">blocked</Pill><div className="muted">{readiness.blocks[a.key]}</div></>}</div></div>
        ))}
      </div>
    </>
  );
}

function ReadinessCard({ readiness, customerId }: { readiness: Readiness | null; customerId: number }) {
  if (!readiness || readiness.ready) return null;
  const missing = readiness.items.filter((i) => !i.ok);
  return (
    <Card title="Not ready to trade yet" sub={`${missing.length} fact${missing.length === 1 ? '' : 's'} still missing`}
          actions={<Button small onClick={() => go(`#/customers/new?resume=${customerId}`)}>Continue onboarding →</Button>}>
      <ul className="timeline">
        {missing.map((i) => <li key={i.key}><strong>{i.title}</strong> — {i.detail}</li>)}
      </ul>
    </Card>
  );
}

function Onboard({ resumeId }: { resumeId: number | null }) {
  const [customerId, setCustomerId] = useState<number | null>(resumeId);
  const [siteId, setSiteId] = useState<number | null>(null);
  const [step, setStep] = useState<number>(resumeId === null ? 0 : -1);   // -1: asking where to resume
  const [gstinTyped, setGstinTyped] = useState('');
  const readiness = useAsk(() => (customerId !== null ? api.view('customers.readiness', { customerId }) : Promise.resolve(null)), [customerId, step], MESSAGES);
  const detail = useAsk(() => (customerId !== null ? api.view('customers.get', { customerId }) : Promise.resolve(null)), [customerId, step], MESSAGES);

  // resuming: land on the first step still missing, once the readiness is known
  if (step === -1 && readiness.data) {
    const target = stepFor(readiness.data.nextStep);
    setStep(target);
  }
  if (customerId !== null && siteId === null && detail.data) {
    const primary = detail.data.sites.find((x) => x.isPrimary) ?? detail.data.sites[0];
    if (primary) setSiteId(primary.siteId);
  }
  const current: OnboardingStep = step < 0 ? 'identity' : ONBOARDING_STEPS[step]!.key;
  const next = () => setStep((n) => Math.min(n + 1, 5));

  return (
    <Page title={customerId === null ? 'New customer' : `Onboarding ${detail.data?.customer.name ?? ''}`}
          sub="Six short steps. You can stop at any time and pick up where you left off."
          actions={customerId !== null ? <Button onClick={() => go(`#/customers/${customerId}`)}>Open the customer</Button> : null}>
      <StepRail current={current} />
      {step === -1 ? <Card><Loading what="where to pick up" /></Card> : null}
      {current === 'identity' && step >= 0 ? <IdentityStep onDone={(c, st, gstin) => { setCustomerId(c); setSiteId(st); setGstinTyped(gstin); setStep(1); }} /> : null}
      {current === 'registration' && customerId !== null ? <RegistrationStep customerId={customerId} gstin={gstinTyped} onDone={next} /> : null}
      {current === 'site' && customerId !== null ? <SiteStep customerId={customerId} siteId={siteId} siteName={detail.data?.sites.find((x) => x.siteId === siteId)?.name ?? 'the primary site'} onDone={next} /> : null}
      {current === 'contacts' && customerId !== null ? <ContactsStep customerId={customerId} onDone={next} /> : null}
      {current === 'terms' && customerId !== null && detail.data ? (
        detail.data.terms.missing
          ? <SetTerms customerId={customerId} terms={detail.data.terms} onDone={next} />
          : <Card title="Agreed rates"><Banner kind="good" title="Agreed rates are already on record." /><Button kind="primary" onClick={next}>Next: review</Button></Card>
      ) : null}
      {current === 'review' && customerId !== null ? (
        <Card title="Review" sub="what is recorded, and what each action still waits on">
          {readiness.data ? <ReviewPanel readiness={readiness.data} /> : <Loading what="the review" />}
          {readiness.data && !readiness.data.ready ? <Button onClick={() => setStep(stepFor(readiness.data!.nextStep))}>Go to the first missing fact</Button> : null}
          <Button kind="primary" onClick={() => go(`#/customers/${customerId}`)}>Open the customer</Button>
        </Card>
      ) : null}
    </Page>
  );
}

function IdentityStep({ onDone }: { onDone: (customerId: number, siteId: number, gstin: string) => void }) {
  const [name, setName] = useState(''); const [stateCode, setStateCode] = useState('');
  const [gstType, setGstType] = useState(''); const [gstin, setGstin] = useState('');
  const [siteName, setSiteName] = useState(''); const [address, setAddress] = useState('');
  const [creditLimit, setCreditLimit] = useState(''); const [deposit, setDeposit] = useState('');
  const runner = useRun(MESSAGES);
  const states = useAsk(() => api.view('states.list', {}), [], MESSAGES);
  const gstinState = gstin.trim().length >= 2 ? gstin.trim().slice(0, 2) : '';

  async function submit() {
    const limit = creditLimit.trim() ? rupeesToPaise(creditLimit) : null;
    const dep = deposit.trim() ? rupeesToPaise(deposit) : null;
    const ok = await runner.run(async () => {
      const r = await api.command('customer.create', {
        name: name.trim(),
        ...(gstin.trim() ? { gstin: gstin.trim().toUpperCase() } : {}),
        ...(stateCode ? { stateCode } : {}),
        ...(gstType ? { gstRegistration: gstType as 'regular' | 'composition' | 'unregistered' | 'sez' | 'other' } : {}),
        ...(limit !== null ? { creditLimitPaise: limit } : {}),
        ...(dep !== null ? { depositPerCylinderPaise: dep } : {}),
        primarySite: { name: siteName.trim(), ...(address.trim() ? { address: address.trim() } : {}) },
      });
      const v = r.value as { customerId: number; siteId: number };
      onDone(v.customerId, v.siteId, gstin.trim().toUpperCase());
      return r;
    });
    if (!ok) return;
  }
  return (
    <Card title="Who they are" sub="the trading name, the state, and how they stand with GST; and the site the cylinders go to">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="ob-name" label="Customer name" value={name} onChange={setName} required autoFocus />
          <Select name="ob-state" label="State" value={stateCode} onChange={setStateCode} placeholder="Choose the state"
                  options={(states.data?.rows ?? []).map((st) => ({ value: st.code, label: `${st.code} · ${st.name}` }))} />
          <Select name="ob-gst-type" label="GST" value={gstType} onChange={setGstType} placeholder="How do they stand with GST?"
                  options={[{ value: 'regular', label: 'Regular — has a GSTIN' }, { value: 'composition', label: 'Composition scheme' },
                            { value: 'unregistered', label: 'Unregistered' }, { value: 'sez', label: 'SEZ unit' }, { value: 'other', label: 'Other' }]} />
          <Text name="ob-gstin" label="GSTIN" value={gstin} onChange={setGstin} help={gstinState && stateCode && gstinState !== stateCode ? `this GSTIN is registered in state ${gstinState}, not ${stateCode}` : '15 characters, or leave blank'} />
        </div>
        <div>
          <Text name="ob-site" label="Primary site" value={siteName} onChange={setSiteName} required help="where the cylinders are delivered" />
          <TextArea name="ob-site-address" label="Site address (as written)" value={address} onChange={setAddress} />
          <Text name="ob-credit" label="Credit limit (₹)" value={creditLimit} onChange={setCreditLimit} help="once the customer owes more than this, no order can be taken until it is paid down or a manager raises the limit" />
          <Text name="ob-deposit" label="Deposit per cylinder (₹)" value={deposit} onChange={setDeposit} help="for reference — nothing is stopped by it yet" />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !name.trim() || !siteName.trim()}>
        {runner.busy ? 'Creating…' : 'Create the customer'}
      </Button>
    </Card>
  );
}

function RegistrationStep({ customerId, gstin, onDone }: { customerId: number; gstin: string; onDone: () => void }) {
  const [legalName, setLegalName] = useState(''); const [from, setFrom] = useState(today());
  const [g, setG] = useState(gstin);
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('customer.recordRegistration', { customerId, gstin: g.trim().toUpperCase(), legalName: legalName.trim(), effectiveFrom: from }));
    if (ok) onDone();
  }
  return (
    <Card title="GST registration" sub="a dated fact: the GSTIN, the legal name it is registered in, and from when">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="ob-reg-gstin" label="GSTIN" value={g} onChange={setG} />
          <Text name="ob-reg-name" label="Legal name on the registration" value={legalName} onChange={setLegalName} help="what goes on the paper" />
        </div>
        <div><Text name="ob-reg-from" label="Effective from" type="date" value={from} onChange={setFrom} /></div>
      </div>
      <div className="toolbar">
        <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !g.trim() || !legalName.trim()}>{runner.busy ? 'Recording…' : 'Record the registration'}</Button>
        <Button onClick={onDone}>No GSTIN — skip</Button>
      </div>
    </Card>
  );
}

function SiteStep({ customerId, siteId, siteName, onDone }: { customerId: number; siteId: number | null; siteName: string; onDone: () => void }) {
  const [line1, setLine1] = useState(''); const [line2, setLine2] = useState(''); const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState(''); const [pin, setPin] = useState('');
  const runner = useRun(MESSAGES);
  const states = useAsk(() => api.view('states.list', {}), [], MESSAGES);
  async function submit() {
    if (siteId === null) return;
    const ok = await runner.run(() => api.command('customer.setAddress', {
      customerId, siteId, kind: 'delivery', line1: line1.trim(),
      ...(line2.trim() ? { line2: line2.trim() } : {}), ...(city.trim() ? { city: city.trim() } : {}),
      ...(stateCode ? { stateCode } : {}), ...(pin.trim() ? { pincode: pin.trim() } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <Card title="Where the cylinders go" sub={`the delivery address of ${siteName}, as the driver needs it`}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="ob-addr-line1" label="Line 1" value={line1} onChange={setLine1} required />
          <Text name="ob-addr-line2" label="Line 2" value={line2} onChange={setLine2} />
          <Text name="ob-addr-city" label="City / town" value={city} onChange={setCity} />
        </div>
        <div>
          <Select name="ob-addr-state" label="State" value={stateCode} onChange={setStateCode} placeholder="—"
                  options={(states.data?.rows ?? []).map((st) => ({ value: st.code, label: `${st.code} · ${st.name}` }))} />
          <Text name="ob-addr-pin" label="PIN" value={pin} onChange={setPin} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !line1.trim() || siteId === null}>{runner.busy ? 'Saving…' : 'Set the delivery address'}</Button>
    </Card>
  );
}

function ContactsStep({ customerId, onDone }: { customerId: number; onDone: () => void }) {
  const [opsName, setOpsName] = useState(''); const [opsPhone, setOpsPhone] = useState('');
  const [accName, setAccName] = useState(''); const [accPhone, setAccPhone] = useState(''); const [accEmail, setAccEmail] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('customer.addContact', { customerId, name: opsName.trim(), role: 'operations', phone: opsPhone.trim(), isPrimary: true });
      if (accName.trim() && (accPhone.trim() || accEmail.trim())) {
        await api.command('customer.addContact', { customerId, name: accName.trim(), role: 'accounts',
          ...(accPhone.trim() ? { phone: accPhone.trim() } : {}), ...(accEmail.trim() ? { email: accEmail.trim() } : {}) });
      }
      return r;
    });
    if (ok) onDone();
  }
  return (
    <Card title="Who to reach" sub="the person the yard calls about a load, and the person accounts sends the bill to">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <h3 className="section">Operations</h3>
          <Text name="ob-ops-name" label="Name" value={opsName} onChange={setOpsName} required />
          <Text name="ob-ops-phone" label="Phone" value={opsPhone} onChange={setOpsPhone} required />
        </div>
        <div>
          <h3 className="section">Accounts (optional)</h3>
          <Text name="ob-acc-name" label="Name" value={accName} onChange={setAccName} />
          <Text name="ob-acc-phone" label="Phone" value={accPhone} onChange={setAccPhone} />
          <Text name="ob-acc-email" label="Email" value={accEmail} onChange={setAccEmail} />
        </div>
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !opsName.trim() || !opsPhone.trim()}>{runner.busy ? 'Adding…' : 'Add the contacts'}</Button>
    </Card>
  );
}
